import React,
{
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
}
from 'react';
import './App.css';
import SpaceDots from './SpaceDots';
import FloatingImages from './FloatingImages';
import IntroOverlay from './IntroOverlay';
import Router,
{
    Route,
    navigate,
    normalizePath,
    replaceNavigate
}
from './Router';
import SubpageFade from './SubpageFade';
import Main from './Main';
import FullMoonClub from './FullMoonClub';
import AboutMe from './AboutMe';
import Discord from './Discord';
import Projects from './Projects';
import Settings from './Settings';
import FRncSuperSecretStrats from './FRncSuperSecretStrats';
import RGL from './RGL';
import SpotTheFloof from './SpotTheFloof';
import ClickTheIdiot from './ClickTheIdiot';
import VolumeControl from './VolumeControl';
import LeoLoadingShit from './LeoLoadingShit';
import masterVolume from './masterVolume';
import
{
    createPreloadedAudio
}
from './audioPreload';
import
{
    isHalloweenMode
}
from './seasonalMode';
import
{
    AchievementsProvider
}
from './achievements/AchievementsContext';
import
{
    Analytics
}
from '@vercel/analytics/react';

const MAIN_BPM_DEBUG_STORAGE_KEY = 'aw24-main-bpm-debug';
const ROOT_UI_REFERENCE_WIDTH = 960;
const ROOT_UI_REFERENCE_HEIGHT = 720;
const ROOT_FLOATING_ARI_DESKTOP_SCALE = 0.8;
const ROOT_FLOATING_ARI_MOBILE_SCALE = 0.5;
const MEDIA_GATE_AUDIO_PROBE_SRC = '/Audio/IntroLoop-Base.ogg';
const MEDIA_GATE_VIDEO_PROBE_SRC = '/Video/HeartIntro-site.mp4';
const MAIN_THEME_EXIT_FADE_MS = 380;
const HALLOWEEN_MODE = isHalloweenMode();
const HALLOWEEN_ENTRY_AUDIO_SRC = '/Audio/EnteringMain-Halloween2.ogg';
const MAIN_ENTRY_SPEED_UP_MS = 1250;
const HALLOWEEN_ENTRY_SPEED_UP_MS = 1500;

function isMainThemeRoute(path)
{
    const normalizedPath = normalizePath(path || '/');
    return normalizedPath === '/main' || normalizedPath === '/FullMoonClub';
}

function isRglRoute(path)
{
    const normalizedPath = normalizePath(path || '/');
    return normalizedPath === '/RGL' || normalizedPath.startsWith('/RGL/');
}

function getMediaAutoplayPolicy()
{
    try
    {
        if (typeof navigator !== 'undefined' && typeof navigator.getAutoplayPolicy === 'function')
        {
            return navigator.getAutoplayPolicy('mediaelement');
        }
    }
    catch (e)
    {}

    return 'unknown';
}

function readViewportSize()
{
    return {
        width: Math.round(window.innerWidth),
        height: Math.round(window.innerHeight),
    };
}

function clamp01(v)
{
    return Math.max(0, Math.min(1, v));
}

function getDetailBaseVolume(detail, fallback = 0.5)
{
    try
    {
        const raw = detail && typeof detail.baseVolume !== 'undefined' ? Number(detail.baseVolume) : NaN;
        return Number.isFinite(raw) ? clamp01(raw) : fallback;
    }
    catch (e)
    {
        return fallback;
    }
}

function getDetailMainStartDelayMs(detail, fallback = 0)
{
    try
    {
        const raw = detail && typeof detail.musicDelayMs !== 'undefined' ? Number(detail.musicDelayMs) : NaN;
        return Number.isFinite(raw) ? Math.max(0, raw) : fallback;
    }
    catch (e)
    {
        return fallback;
    }
}

function getDetailOnBeatEntry(detail)
{
    try
    {
        const enabled = !!(detail && (detail.onBeatEntry || detail.onBeatIntro));
        if (!enabled) return null;

        const bpmRaw = detail && typeof detail.onBeatBpm !== 'undefined' ? Number(detail.onBeatBpm) : NaN;
        const beatsRaw = detail && typeof detail.onBeatCount !== 'undefined' ? Number(detail.onBeatCount) : NaN;
        if (!Number.isFinite(bpmRaw) || bpmRaw <= 0) return null;
        if (!Number.isFinite(beatsRaw) || beatsRaw < 0) return null;

        return {
            bpm: bpmRaw,
            beats: beatsRaw,
        };
    }
    catch (e)
    {
        return null;
    }
}

function getDetailRestartFromIntro(detail)
{
    try
    {
        return !!(detail && detail.restartFromIntro);
    }
    catch (e)
    {
        return false;
    }
}

function shouldPlayMainThemeIntro(
{
    nextRoute,
    from,
    routeFrom,
    restartFromIntro = false,
    previousSelectionRoute = null
})
{
    const targetRoute = normalizePath(nextRoute || '/main');
    const originRoute = from ? normalizePath(from) : null;
    const previousRoute = routeFrom ? normalizePath(routeFrom) : null;
    const previousThemeRoute = previousSelectionRoute ? normalizePath(previousSelectionRoute) : null;

    if (originRoute === '/discord' || previousRoute === '/discord') return false;
    if (previousThemeRoute && isMainThemeRoute(previousThemeRoute) && previousThemeRoute !== targetRoute) return true;
    if (originRoute === '/' || previousRoute === '/') return true;
    if (originRoute && isMainThemeRoute(originRoute) && originRoute !== targetRoute) return true;
    if (previousRoute && isMainThemeRoute(previousRoute) && previousRoute !== targetRoute) return true;

    return !!restartFromIntro && !!originRoute && originRoute !== targetRoute;
}

function hasUnlockedAchievement(achievementId)
{
    try
    {
        const raw = localStorage.getItem('aw24_achievements_unlocked_v1');
        if (!raw) return false;

        const parsed = JSON.parse(raw);
        const unlocked = Array.isArray(parsed) ?
            parsed :
            (parsed && Array.isArray(parsed.unlocked) ? parsed.unlocked : []);

        return unlocked.includes(achievementId);
    }
    catch (e)
    {
        return false;
    }
}

function useTyping(words, typingSpeed = 100, pause = 800, deletingSpeed = 40, enabled = true, onCycleComplete = null)
// this shit handles the typing effect on the front main page
// to edit text go to public > messages.json
// TEMPLATE: { "text": "Custom message!", "durationMs": 1200, "typingSpeed": 60, "deletingSpeed": 30 },
// this is the format to add more messages, current atributes are text, durationMs, typingSpeed, deletingSpeed
// should be self explanatory what they do, if not then i think ur stupid
{
    const [text, setText] = useState('');
    const [wordIndex, setWordIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const prevEnabledRef = useRef(enabled);
    const onCycleCompleteRef = useRef(onCycleComplete);

    useEffect(() =>
    {
        onCycleCompleteRef.current = onCycleComplete;
    }, [onCycleComplete]);

    useEffect(() =>
    {
        if (enabled && !prevEnabledRef.current)
        {
            setText('');
            setWordIndex(0);
            setIsDeleting(false);
        }
        prevEnabledRef.current = enabled;
    }, [enabled]);

    useEffect(() =>
    {
        if (!enabled) return;
        if (!words || words.length === 0) return;

        let timeout;
        const entry = words[wordIndex % words.length];
        const msg = typeof entry === 'string' ?
        {
            text: entry
        } : (entry ||
        {});
        const currentText = msg.text || '';
        const tSpeed = typeof msg.typingSpeed === 'number' ? msg.typingSpeed : typingSpeed;
        const dSpeed = typeof msg.deletingSpeed === 'number' ? msg.deletingSpeed : deletingSpeed;
        const dwell = typeof msg.durationMs === 'number' ? msg.durationMs : pause;

        if (!isDeleting)
        {
            timeout = setTimeout(() =>
            {
                setText((current) => current + (currentText.charAt(current.length) || ''));
            }, tSpeed);

            if (text === currentText)
            {
                timeout = setTimeout(() => setIsDeleting(true), dwell);
            }
        }
        else
        {
            timeout = setTimeout(() =>
            {
                setText((current) => current.slice(0, -1));
            }, dSpeed);

            if (text === '')
            {
                if (words.length > 0 && ((wordIndex + 1) % words.length) === 0)
                {
                    try
                    {
                        if (typeof onCycleCompleteRef.current === 'function') onCycleCompleteRef.current();
                    }
                    catch (e)
                    {}
                }
                setIsDeleting(false);
                setWordIndex((i) => i + 1);
            }
        }

        return () => clearTimeout(timeout);
    }, [text, isDeleting, wordIndex, words, typingSpeed, pause, deletingSpeed, enabled]);

    return text;
}

function AppContent()
{
    const initialPathRef = useRef(normalizePath(window.location.pathname || '/'));
    const HEART_BEAT_BPM = 155;
    const HEART_BEAT_SCALE_DELTA = 0.16; // intensity 1.0 => scale(1 + delta)

    const defaultMessages = // default error message if messages.json is missing, check line 98 for format
        [
            "Missing 'messages.json' file.",
            "Check in 'public' folder for file.",
            "Check line 98 of src/App.js for format.",
        ];
    const [remoteMessages, setRemoteMessages] = useState([]);
    useEffect(() =>
    {
        let cancelled = false;
        const load = async () =>
        {
            try
            {
                const res = await fetch('/messages.json');
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) setRemoteMessages(data);
            }
            catch (e)
            {
                if (!cancelled) setRemoteMessages([]);
            }
        };
        load();
        return () =>
        {
            cancelled = true;
        };
    }, []);

    const audioRef = useRef(null);
    const hover1Ref = useRef(null);
    const hover2Ref = useRef(null);
    const heartImgRef = useRef(null);

    // Heartbeat scheduler state
    const heartBeatTimeoutRef = useRef(null);
    const heartBeatRafRef = useRef(null);
    const heartBeatTokenRef = useRef(0);
    const webLoopStartAtRef = useRef(null);
    const heartBeatClockNowRef = useRef(null);
    const heartBeatStartTimeRef = useRef(null);
    const heartBeatStageRef = useRef('base');
    const [autoplayBlocked, setAutoplayBlocked] = useState(false);
    const [videoAutoplayBlocked, setVideoAutoplayBlocked] = useState(false);
    const [requireEnable, setRequireEnable] = useState(false);
    const [enableError, setEnableError] = useState('');
    const [introDone, setIntroDone] = useState(false);
    const [fadeStarted, setFadeStarted] = useState(false);
    const [mountSpace, setMountSpace] = useState(false);
    const [headerVisible, setHeaderVisible] = useState(false);
    const [headerMounted, setHeaderMounted] = useState(true);
    const [ariMounted, setAriMounted] = useState(true);
    const [ariVisible, setAriVisible] = useState(true);
    const [currentPath, setCurrentPath] = useState(normalizePath(window.location.pathname || '/'));
    const [viewportSize, setViewportSize] = useState(() => readViewportSize());
    const previousPathRef = useRef(normalizePath(window.location.pathname || '/'));
    const [spaceMotion, setSpaceMotion] = useState(() => (isMainThemeRoute(window.location.pathname || '/') ? 'centerOut' : 'fall'));
    const [showLeoLoader, setShowLeoLoader] = useState(false);
    const [leoLoaderMode, setLeoLoaderMode] = useState('loading');
    const [leoLoaderPosition, setLeoLoaderPosition] = useState('bottom-left');
    const leoLoaderActiveRef = useRef(false);
    const [showMainBpmDebug, setShowMainBpmDebug] = useState(() =>
    {
        try
        {
            return localStorage.getItem(MAIN_BPM_DEBUG_STORAGE_KEY) === '1';
        }
        catch (e)
        {
            return false;
        }
    });
    const [mainBpmDebugSnapshot, setMainBpmDebugSnapshot] = useState(
    {
        className: 'Default',
        enabled: false,
        bpm: null,
        currentBeat: null,
        targetBeat: null,
        loopStarted: false,
    });
    const isSpotTheFloofRoute = currentPath === '/SpotTheFloof';
    const isFRncRoute = currentPath === '/FR*nc' || currentPath === '/PCUplaybook';
    const SPACE_FADE_MS = 700; // should roughly match IntroOverlay FADE_SECONDS * 1000
    const HEADER_DELAY_MS = 160; // short delay after space fade completes before header appears
    const messageCycleReportedRef = useRef(false);
    const leoLoaderTimerRef = useRef(null);
    const mobileUserAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(mobileUserAgent);
    const isCoarsePointer = typeof window !== 'undefined' && window.matchMedia ?
        window.matchMedia('(pointer: coarse)').matches :
        false;
    const shouldSkipRootIntroOnMobile = isMobileDevice && currentPath === '/';
    const rootUiScale = Math.min(
        1,
        viewportSize.width / ROOT_UI_REFERENCE_WIDTH,
        viewportSize.height / ROOT_UI_REFERENCE_HEIGHT
    );
    const rootFloatingAriScale = isCoarsePointer ?
        ROOT_FLOATING_ARI_MOBILE_SCALE :
        ROOT_FLOATING_ARI_DESKTOP_SCALE;

    const rootHeaderStageStyle = {
        width: `${ROOT_UI_REFERENCE_WIDTH}px`,
        height: `${ROOT_UI_REFERENCE_HEIGHT}px`,
        position: 'relative',
        transform: `scale(${rootUiScale})`,
        transformOrigin: 'center center',
    };

    const rootHeaderStyle = {
        minHeight: '100%',
        width: '100%',
        fontSize: '28px',
    };

    const rootHeartStyle = {
        height: '360px',
    };

    const rootCopyrightStyle = {
        position: 'fixed',
        left: '50%',
        bottom: `${Math.max(12, Math.round(24 * rootUiScale))}px`,
        transform: 'translateX(-50%)',
        color: 'rgba(230,230,230,0.85)',
        fontSize: `${Math.max(10, Math.round(12 * rootUiScale))}px`,
        zIndex: 2,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
    };

    const handleMainMessageCycleComplete = useCallback(() =>
    {
        if (messageCycleReportedRef.current) return;

        if (hasUnlockedAchievement('great-listener'))
        {
            messageCycleReportedRef.current = true;
            return;
        }

        messageCycleReportedRef.current = true;
        try
        {
            window.dispatchEvent(new CustomEvent('main-messages-cycle-complete'));
        }
        catch (e)
        {}
    }, []);

    useEffect(() =>
    {
        const onAchievementsReset = () =>
        {
            messageCycleReportedRef.current = false;
        };

        window.addEventListener('achievements-reset', onAchievementsReset);
        return () => window.removeEventListener('achievements-reset', onAchievementsReset);
    }, []);

    useEffect(() =>
    {
        const onResize = () =>
        {
            setViewportSize((previousSize) =>
            {
                const nextSize = readViewportSize();
                if (previousSize.width === nextSize.width && previousSize.height === nextSize.height)
                {
                    return previousSize;
                }
                return nextSize;
            });
        };

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // typing starts only after UI fade begins on root
    const typed = useTyping(
        remoteMessages.length ? remoteMessages : defaultMessages,
        60,
        700,
        30,
        (introDone && fadeStarted && currentPath === '/'),
        handleMainMessageCycleComplete
    );

    // common audio creation to avoid duplicated new audio shit cuz itll be annoying
    // big news! AHHHHHHHHH
    const createAudioElement = useCallback((src = '/Audio/IntroLoop-Base.ogg', baseVolume = 0.5, applyMaster = true) =>
    {
        return createPreloadedAudio(src,
        {
            baseVolume,
            loop: true,
            applyMaster,
            muted: false,
        });
    }, []);

    useEffect(() =>
    {
        if (!HALLOWEEN_MODE || window.__aw24HalloweenEntryAudio) return;

        window.__aw24HalloweenEntryAudio = createPreloadedAudio(HALLOWEEN_ENTRY_AUDIO_SRC,
        {
            baseVolume: 1.0,
            loop: false,
        });
    }, []);

    // apply persisted master volume to any existing media elements on first mount
    useEffect(() =>
    {
        try
        {
            masterVolume.applyToAllMedia();
        }
        catch (e)
        {}
    }, []);

    const getOrCreateAudio = useCallback(() =>
    {
        if (!audioRef.current)
        {
            // create homepage loop
            audioRef.current = createAudioElement('/Audio/IntroLoop-Base.ogg', 0.75, true);
            // create hover variants (start muted at volume 0) without auto master apply
            try
            {
                hover1Ref.current = createAudioElement('/Audio/IntroLoop-Hover1.ogg', 0.75, false);
                hover1Ref.current._hoverGain = 0;
                hover1Ref.current.volume = 0;
                // add shit to master volume changes for hover element
                try
                {
                    hover1Ref.current._masterUnsub = masterVolume.subscribe((m) =>
                    {
                        try
                        {
                            hover1Ref.current.volume = Math.max(0, Math.min(1, (hover1Ref.current._baseVolume || 0.75) * m * (hover1Ref.current._hoverGain || 0)));
                        }
                        catch (e)
                        {}
                    });
                }
                catch (e)
                {}
            }
            catch (e)
            {
                hover1Ref.current = null;
            }
            try
            {
                hover2Ref.current = createAudioElement('/Audio/IntroLoop-Hover2.ogg', 0.75, false);
                hover2Ref.current._hoverGain = 0;
                hover2Ref.current.volume = 0;
                try
                {
                    hover2Ref.current._masterUnsub = masterVolume.subscribe((m) =>
                    {
                        try
                        {
                            hover2Ref.current.volume = Math.max(0, Math.min(1, (hover2Ref.current._baseVolume || 0.75) * m * (hover2Ref.current._hoverGain || 0)));
                        }
                        catch (e)
                        {}
                    });
                }
                catch (e)
                {}
            }
            catch (e)
            {
                hover2Ref.current = null;
            }
        }
        return audioRef.current;
    }, [createAudioElement]);

    // shit to make the audios for homepage not lag
    const audioCtxRef = useRef(null);
    const webBuffersRef = useRef(
    {
        base: null,
        h1: null,
        h2: null
    });
    const webSrcRef = useRef(
    {
        base: null,
        h1: null,
        h2: null
    });
    const webGainsRef = useRef(
    {
        master: null,
        base: null,
        h1: null,
        h2: null
    });
    const webLoadedRef = useRef(false);

    const setLeoLoaderActivity = useCallback((active) =>
    {
        if (active)
        {
            if (leoLoaderActiveRef.current) return;
            leoLoaderActiveRef.current = true;
            if (leoLoaderTimerRef.current)
            {
                clearTimeout(leoLoaderTimerRef.current);
                leoLoaderTimerRef.current = null;
            }
            setLeoLoaderMode('loading');
            setLeoLoaderPosition('bottom-left');
            setShowLeoLoader(true);
            return;
        }

        if (!leoLoaderActiveRef.current) return;
        leoLoaderActiveRef.current = false;
        if (leoLoaderTimerRef.current)
        {
            clearTimeout(leoLoaderTimerRef.current);
        }
        leoLoaderTimerRef.current = setTimeout(() =>
        {
            leoLoaderTimerRef.current = null;
            setLeoLoaderMode('finished');
            setLeoLoaderPosition('bottom-left');
            setShowLeoLoader(true);
        }, 220);
    }, []);

    const preloadWebAudio = useCallback(async () =>
    {
        if (webLoadedRef.current) return;
        setLeoLoaderActivity(true);
        try
        {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            if (!AudioContext) return;
            const ctx = audioCtxRef.current || new AudioContext();
            audioCtxRef.current = ctx;

            // master gain to apply masterVolume easily
            const masterGain = ctx.createGain();
            masterGain.gain.value = masterVolume.getMasterVolume();
            masterGain.connect(ctx.destination);
            webGainsRef.current.master = masterGain;

            const baseGain = ctx.createGain();
            baseGain.gain.value = 0.0;
            baseGain.connect(masterGain);
            const h1Gain = ctx.createGain();
            h1Gain.gain.value = 0.0;
            h1Gain.connect(masterGain);
            const h2Gain = ctx.createGain();
            h2Gain.gain.value = 0.0;
            h2Gain.connect(masterGain);
            webGainsRef.current.base = baseGain;
            webGainsRef.current.h1 = h1Gain;
            webGainsRef.current.h2 = h2Gain;

            const fetchAndDecode = async (url) =>
            {
                const res = await fetch(url);
                const ab = await res.arrayBuffer();
                return await ctx.decodeAudioData(ab);
            };

            const [baseBuf, h1Buf, h2Buf] = await Promise.all([
                fetchAndDecode('/Audio/IntroLoop-Base.ogg'),
                fetchAndDecode('/Audio/IntroLoop-Hover1.ogg'),
                fetchAndDecode('/Audio/IntroLoop-Hover2.ogg'),
            ]);
            webBuffersRef.current.base = baseBuf;
            webBuffersRef.current.h1 = h1Buf;
            webBuffersRef.current.h2 = h2Buf;
            webLoadedRef.current = true;

            // add shit to master volume changes
            try
            {
                webGainsRef.current._masterUnsub = masterVolume.subscribe((m) =>
                {
                    try
                    {
                        if (webGainsRef.current.master) webGainsRef.current.master.gain.setValueAtTime(m, audioCtxRef.current.currentTime);
                    }
                    catch (e)
                    {}
                });
            }
            catch (e)
            {}
        }
        catch (e)
        {
            // fail silently, keep HTMLAudio fallback
            console.warn('WebAudio preload failed', e);
            webLoadedRef.current = false;
        }
        finally
        {
            setLeoLoaderActivity(false);
        }
    }, [setLeoLoaderActivity]);

    const startWebAudioLoop = useCallback(() =>
    {
        const ctx = audioCtxRef.current;
        if (!ctx || !webLoadedRef.current) return false;
        if (webSrcRef.current.base) return true; // already started

        try
        {
            const now = ctx.currentTime;
            const startAt = now + 0.05;
            webLoopStartAtRef.current = startAt;

            // restore master volume since i forgor to do that earlier when user comes back to homepage
            // this is to hopefully fix dumb sequence audio transition
            try
            {
                const master = webGainsRef.current.master;
                if (master && typeof masterVolume.getMasterVolume === 'function')
                {
                    const mv = masterVolume.getMasterVolume();
                    master.gain.cancelScheduledValues(now);
                    master.gain.setValueAtTime(mv, now);
                }
            }
            catch (e)
            {}

            const makeSource = (buffer, gainNode) =>
            {
                const src = ctx.createBufferSource();
                src.buffer = buffer;
                src.loop = true;
                src.connect(gainNode);
                src.start(startAt);
                return src;
            };

            if (webBuffersRef.current.base) webSrcRef.current.base = makeSource(webBuffersRef.current.base, webGainsRef.current.base);
            if (webBuffersRef.current.h1) webSrcRef.current.h1 = makeSource(webBuffersRef.current.h1, webGainsRef.current.h1);
            if (webBuffersRef.current.h2) webSrcRef.current.h2 = makeSource(webBuffersRef.current.h2, webGainsRef.current.h2);

            // make sure audio is at 0.75 volume when starting
            try
            {
                webGainsRef.current.base.gain.setValueAtTime(0.75, startAt);
            }
            catch (e)
            {}
            return true;
        }
        catch (e)
        {
            console.warn('startWebAudioLoop failed', e);
            return false;
        }
    }, []);

    const stopHeartBeat = useCallback(() =>
    {
        heartBeatTokenRef.current += 1;
        if (heartBeatTimeoutRef.current)
        {
            try
            {
                clearTimeout(heartBeatTimeoutRef.current);
            }
            catch (e)
            {}
            heartBeatTimeoutRef.current = null;
        }
        if (heartBeatRafRef.current)
        {
            try
            {
                cancelAnimationFrame(heartBeatRafRef.current);
            }
            catch (e)
            {}
            heartBeatRafRef.current = null;
        }
        heartBeatClockNowRef.current = null;
        heartBeatStartTimeRef.current = null;
    }, []);

    const triggerHeartBeat = useCallback((intensity = 1, stageName = 'base') =>
    {
        const el = heartImgRef.current;
        if (!el) return;
        const clamp01 = (v) => Math.max(0, Math.min(1, v));
        const s = 1 + HEART_BEAT_SCALE_DELTA * clamp01(intensity);
        const isBase = stageName === 'base';
        const durationMs = isBase ? 1400 : 520;
        const easing = isBase ? 'cubic-bezier(0, 0.55, 0.12, 1)' : 'cubic-bezier(0, 0.7, 0.15, 1)';
        try
        {
            // Web Animations API gives better timing than class toggles
            el.animate(
                [
                    {
                        transform: 'scale(1)'
                    },
                    // sudden pop
                    {
                        transform: `scale(${s})`,
                        offset: 0.10
                    },
                    // slow diminish
                    {
                        transform: 'scale(1)'
                    },
                ],
                {
                    duration: durationMs,
                    easing,
                    fill: 'none'
                }
            );
            return;
        }
        catch (e)
        {
            // fallback: class toggle
            try
            {
                try
                {
                    el.style.setProperty('--hb-scale', String(s));
                }
                catch (e3)
                {}
                try
                {
                    el.style.setProperty('--hb-duration', `${durationMs}ms`);
                }
                catch (e4)
                {}
                el.classList.remove('Heart-image--beat');
                // force reflow so animation restarts
                void el.offsetWidth;
                el.classList.add('Heart-image--beat');
            }
            catch (e2)
            {}
        }
    }, []);

    const startHeartBeat = useCallback((clockNowSeconds, startTimeSeconds, stage = 'base') =>
    {
        try
        {
            if (typeof window !== 'undefined' && window.matchMedia)
            {
                const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
                if (reduce && reduce.matches) return;
            }
        }
        catch (e)
        {}

        stopHeartBeat();
        heartBeatClockNowRef.current = clockNowSeconds;
        heartBeatStartTimeRef.current = startTimeSeconds;
        heartBeatStageRef.current = stage;
        const token = heartBeatTokenRef.current;
        const bpm = HEART_BEAT_BPM;
        const period = 60 / bpm;
        const lead = 0.03; // schedule slightly early then spin to the target for tighter timing

        const scheduleNext = () =>
        {
            if (heartBeatTokenRef.current !== token) return;
            let now = 0;
            try
            {
                now = clockNowSeconds();
            }
            catch (e)
            {
                now = 0;
            }
            if (!Number.isFinite(now) || !Number.isFinite(startTimeSeconds)) return;

            // beatNumber is 1 at startTimeSeconds; schedule future beat boundaries only
            const minBeat = (now <startTimeSeconds) ?
                1 :
                (Math.floor((now - startTimeSeconds) / period) + 2);

            let nextBeatNumber = null;
            let intensity = 1.0;
            const stageNow = heartBeatStageRef.current || stage;

            if (stageNow === 'hover1')
            {
                // Pattern (only while hover1 is active):
                // - full hit on beat 1 of each 16-beat cycle
                // - half hit on beats 3, 5, 7, 9, 11, 13, 15
                const nextNormal = 1 + Math.ceil((minBeat - 1) / 16) * 16;
                // next odd beat >= minBeat
                let nextHalf = (minBeat % 2 === 1) ? minBeat : (minBeat + 1);
                // exclude downbeats (beatNumber % 16 === 1)
                if (nextHalf % 16 === 1) nextHalf += 2;
                if (nextNormal <= nextHalf)
                {
                    nextBeatNumber = nextNormal;
                    intensity = 1.0;
                }
                else
                {
                    nextBeatNumber = nextHalf;
                    intensity = 0.5;
                }
            }
            else if (stageNow === 'hover2')
            {
                // Pattern (only while hover2 is active):
                // 1 full, half on other odd beats, quarter on even beats
                nextBeatNumber = minBeat;
                const mod16 = ((nextBeatNumber - 1) % 16) + 1; // 1..16
                if (mod16 === 1) intensity = 1.0;
                else if (nextBeatNumber % 2 === 1) intensity = 0.5;
                else intensity = 0.25;
            }
            else
            {
                // Base stage: full hit on beat 1 of each 16-beat cycle
                nextBeatNumber = 1 + Math.ceil((minBeat - 1) / 16) * 16;
                intensity = 1.0;
            }

            if (!Number.isFinite(nextBeatNumber) || nextBeatNumber < 1) nextBeatNumber = 1;
            const target = startTimeSeconds + (nextBeatNumber - 1) * period;
            const delayMs = Math.max(0, (target - now - lead) * 1000);

            heartBeatTimeoutRef.current = setTimeout(() =>
            {
                const spin = () =>
                {
                    if (heartBeatTokenRef.current !== token) return;
                    let t = 0;
                    try
                    {
                        t = clockNowSeconds();
                    }
                    catch (e)
                    {
                        t = 0;
                    }
                    if (t + 0.001 <target)
                    {
                        heartBeatRafRef.current = requestAnimationFrame(spin);
                        return;
                    }
                    heartBeatRafRef.current = null;
                    triggerHeartBeat(intensity, stageNow);
                    scheduleNext();
                };
                spin();
            }, delayMs);
        };

        scheduleNext();
    }, [HEART_BEAT_BPM, stopHeartBeat, triggerHeartBeat]);

    const setHeartBeatStage = useCallback((stage) =>
    {
        const nextStage = stage === 'hover2' ? 'hover2' : (stage === 'hover1' ? 'hover1' : 'base');
        heartBeatStageRef.current = nextStage;

        // If heartbeat is already running, restart scheduler with new stage (same clock + startTime)
        const clockNow = heartBeatClockNowRef.current;
        const startAt = heartBeatStartTimeRef.current;
        if (typeof clockNow === 'function' && Number.isFinite(startAt))
        {
            try
            {
                startHeartBeat(clockNow, startAt, nextStage);
            }
            catch (e)
            {}
        }
    }, [startHeartBeat]);

    const tryStartHeartBeatFromWebAudio = useCallback(() =>
    {
        try
        {
            if (!audioCtxRef.current) return false;
            const startAt = webLoopStartAtRef.current;
            if (!Number.isFinite(startAt)) return false;
            startHeartBeat(() => audioCtxRef.current.currentTime, startAt, heartBeatStageRef.current || 'base');
            return true;
        }
        catch (e)
        {
            return false;
        }
    }, [startHeartBeat]);

    const stopWebAudioLoop = useCallback((duration = 0) =>
    {
        const ctx = audioCtxRef.current;
        if (!ctx || !webLoadedRef.current) return;
        try
        {
            const master = webGainsRef.current.master;
            const now = ctx.currentTime;
            if (duration > 0)
            {
                master.gain.cancelScheduledValues(now);
                master.gain.setValueAtTime(master.gain.value, now);
                master.gain.linearRampToValueAtTime(0, now + duration / 1000);
            }
            const stopAfter = now + (duration ? duration / 1000 + 0.02 : 0.02);
            setTimeout(() =>
            {
                ['base', 'h1', 'h2'].forEach((k) =>
                {
                    try
                    {
                        if (webSrcRef.current[k])
                        {
                            webSrcRef.current[k].stop();
                            webSrcRef.current[k].disconnect();
                            webSrcRef.current[k] = null;
                        }
                    }
                    catch (e)
                    {}
                });
            }, (stopAfter - ctx.currentTime) * 1000 + 10);
        }
        catch (e)
        {}
    }, []);

    // audio fade control refs
    const fadeRafRef = useRef(null);
    const desiredVolumeRef = useRef(0.75);
    const allowPlayOnFadeRef = useRef(false);
    const playedSinceFadeRef = useRef(false);

    const playLoopImmediate = React.useCallback(async () =>
    {
        try
        {
            // I JUST FIXED THIS SHIT WHY DID IT BREAK
            if (!introDone) return;
            // prefer WebAudio gapless playback when buffers are loaded
            try
            {
                if (webLoadedRef.current && audioCtxRef.current)
                {
                    // resume shit if it was suspended
                    try
                    {
                        if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
                    }
                    catch (e)
                    {}
                    const started = startWebAudioLoop();
                    if (started)
                    {
                        // heartbeat starts on the exact same WebAudio start timestamp
                        try
                        {
                            tryStartHeartBeatFromWebAudio();
                        }
                        catch (e)
                        {}
                        setAutoplayBlocked(false);
                        playedSinceFadeRef.current = true;
                        return;
                    }
                }
            }
            catch (e)
            {}

            const a = getOrCreateAudio();
            // restore base volume for playback and apply master multiplier
            try
            {
                a._baseVolume = desiredVolumeRef.current;
            }
            catch (e)
            {}
            try
            {
                masterVolume.applyToElement(a);
            }
            catch (e)
            {}
            try
            {
                a.currentTime = 0;
            }
            catch (e)
            {}

            const perfStart = (typeof performance !== 'undefined' && performance.now) ? (performance.now() / 1000) : (Date.now() / 1000);
            const toPlay = [a.play && a.play()];
            // attempt to play hover variants as well, should start muted
            const h1 = hover1Ref.current;
            const h2 = hover2Ref.current;
            if (h1)
            {
                try
                {
                    h1.currentTime = 0;
                }
                catch (e)
                {}
                toPlay.push(h1.play && h1.play());
            }
            if (h2)
            {
                try
                {
                    h2.currentTime = 0;
                }
                catch (e)
                {}
                toPlay.push(h2.play && h2.play());
            }

            const results = await Promise.allSettled(toPlay);
            const anySucceeded = results.some(r => r.status === 'fulfilled');
            if (!anySucceeded)
            {
                console.warn('Autoplay blocked when attempting immediate play (all tracks)', results);
                setAutoplayBlocked(true);
                setRequireEnable(true);
            }
            else
            {
                // heartbeat aligns with the moment we attempted to start playback
                try
                {
                    startHeartBeat(() => ((typeof performance !== 'undefined' && performance.now) ? (performance.now() / 1000) : (Date.now() / 1000)), perfStart, heartBeatStageRef.current || 'base');
                }
                catch (e)
                {}
                setAutoplayBlocked(false);
                playedSinceFadeRef.current = true;
            }
        }
        catch (err)
        {
            console.warn('Start sequence failed', err);
            setAutoplayBlocked(true);
            setRequireEnable(true);
        }
    }, [getOrCreateAudio, introDone, startWebAudioLoop, startHeartBeat, tryStartHeartBeatFromWebAudio]);

    // stop heartbeat when leaving the root page or when header is hidden
    useEffect(() =>
    {
        if (currentPath !== '/' || !headerVisible || !fadeStarted)
        {
            stopHeartBeat();
        }
    }, [currentPath, headerVisible, fadeStarted, stopHeartBeat]);

    // fade audio to 0 over duration, then pause and reset currentTime
    const fadeOutAndStopAudio = React.useCallback((duration = 400) =>
    {
        // stop heartbeat immediately when we start fading out
        try
        {
            stopHeartBeat();
        }
        catch (e)
        {}
        // prefer WebAudio stop for gapless playback
        try
        {
            if (audioCtxRef.current && webLoadedRef.current)
            {
                // for some reason, having a return here caused a bug where audio would not stop
                // when a user navigated refrshed the page and returned to / to not allow
                // audio to stop when pressing start again
                stopWebAudioLoop(duration);
            }
        }
        catch (e)
        {}

        const audio = audioRef.current;
        if (!audio) return;
        // cancel any weird shit going on 
        if (fadeRafRef.current)
        {
            cancelAnimationFrame(fadeRafRef.current);
            fadeRafRef.current = null;
        }
        const start = performance.now();
        const from = audio.volume;
        const step = (now) =>
        {
            const t = Math.min(1, (now - start) / duration);
            // linear fade to 0
            audio.volume = Math.max(0, Math.min(1, from * (1 - t)));
            if (t < 1)
            {
                fadeRafRef.current = requestAnimationFrame(step);
            }
            else
            {
                fadeRafRef.current = null;
                try
                {
                    audio.pause();
                    try
                    {
                        audio.currentTime = 0;
                    }
                    catch (e)
                    {}
                    // keep hover tracks playing (even at volume 0) to avoid playback delays
                    // restore base volume for the next time we start playback and apply master multiplier
                    try
                    {
                        audio._baseVolume = desiredVolumeRef.current;
                    }
                    catch (e)
                    {}
                    try
                    {
                        masterVolume.applyToElement(audio);
                    }
                    catch (e)
                    {}
                }
                catch (e)
                {}
            }
        };
        fadeRafRef.current = requestAnimationFrame(step);
    }, [stopWebAudioLoop, stopHeartBeat]);

    // start audio and try to play only after intro has completed
    useEffect(() =>
    {
        if (!introDone) return;
        // if audio already exists (preloaded during fade), reuse it
        const audio = getOrCreateAudio();

        let triedMp3 = false;

        // only preload here. Actual playback should only start when the
        // visual UI fade starts (fadeStarted) so we do not call play() here
        const onError = () =>
        {
            console.warn('Audio element error for', audio.src);
            if (!triedMp3)
            {
                triedMp3 = true;
                audio.src = '/loop.mp3';
                // no auto-play here, just update src for future shit
            }
        };

        audio.addEventListener('error', onError);

        const onVisibility = () =>
        {
            /* noop: we don't auto-play during intro */ };
        const onFocus = () =>
        {
            /* noop */ };
        const onLoad = () =>
        {
            /* noop */ };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onFocus);
        window.addEventListener('load', onLoad);

        // capture current references for cleanup to satisfy exhaustive-deps
        const wgSnapshot = webGainsRef.current;
        const ctxSnapshot = audioCtxRef.current;

        return () =>
        {
            audio.removeEventListener('error', onError);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('load', onLoad);
            if (audioRef.current)
            {
                try
                {
                    audioRef.current.pause();
                }
                catch (e)
                {}
                try
                {
                    audioRef.current.src = '';
                }
                catch (e)
                {}
                audioRef.current = null;
            }
            if (hover1Ref.current)
            {
                try
                {
                    if (hover1Ref.current._hoverGainRaf) cancelAnimationFrame(hover1Ref.current._hoverGainRaf);
                }
                catch (e)
                {}
                try
                {
                    if (hover1Ref.current._masterUnsub) hover1Ref.current._masterUnsub();
                }
                catch (e)
                {}
                try
                {
                    hover1Ref.current.pause();
                }
                catch (e)
                {}
                try
                {
                    hover1Ref.current.src = '';
                }
                catch (e)
                {}
                hover1Ref.current = null;
            }
            if (hover2Ref.current)
            {
                try
                {
                    if (hover2Ref.current._hoverGainRaf) cancelAnimationFrame(hover2Ref.current._hoverGainRaf);
                }
                catch (e)
                {}
                try
                {
                    if (hover2Ref.current._masterUnsub) hover2Ref.current._masterUnsub();
                }
                catch (e)
                {}
                try
                {
                    hover2Ref.current.pause();
                }
                catch (e)
                {}
                try
                {
                    hover2Ref.current.src = '';
                }
                catch (e)
                {}
                hover2Ref.current = null;
            }
            // stop and cleanup WebAudio if present
            try
            {
                if (webLoadedRef.current) stopWebAudioLoop(0);
            }
            catch (e)
            {}
            try
            {
                if (wgSnapshot && wgSnapshot._masterUnsub) wgSnapshot._masterUnsub();
            }
            catch (e)
            {}
            try
            {
                if (ctxSnapshot)
                {
                    try
                    {
                        ctxSnapshot.close();
                    }
                    catch (e)
                    {}
                    if (audioCtxRef.current === ctxSnapshot) audioCtxRef.current = null;
                }
            }
            catch (e)
            {}
        };
    }, [introDone, getOrCreateAudio, stopWebAudioLoop]);

    useEffect(() =>
    {
        if (!introDone)
        {
            setLeoLoaderActivity(true);
        }
        else
        {
            setLeoLoaderActivity(false);
        }
    }, [introDone, setLeoLoaderActivity]);

    // listen for route changes to coordinate UI transitions
    useEffect(() =>
    {
        const onNav = () =>
        {
            const nextPath = normalizePath(window.location.pathname || '/');
            setCurrentPath((prevPath) =>
            {
                previousPathRef.current = prevPath;
                return nextPath;
            });
        };
        const showLeoLoaderForNavigation = () =>
        {
            setLeoLoaderActivity(true);
        };
        const finishLeoLoaderForNavigation = () =>
        {
            setLeoLoaderActivity(false);
        };
        const hideLeoLoaderForNavigation = () =>
        {
            if (leoLoaderTimerRef.current)
            {
                clearTimeout(leoLoaderTimerRef.current);
                leoLoaderTimerRef.current = null;
            }
            leoLoaderActiveRef.current = false;
            setShowLeoLoader(false);
        };
        const scheduleLeoLoaderFinish = () =>
        {
            if (leoLoaderTimerRef.current)
            {
                clearTimeout(leoLoaderTimerRef.current);
            }
            leoLoaderTimerRef.current = setTimeout(() =>
            {
                leoLoaderTimerRef.current = null;
                finishLeoLoaderForNavigation();
            }, 280);
        };

        window.addEventListener('popstate', () =>
        {
            onNav();
            showLeoLoaderForNavigation();
            scheduleLeoLoaderFinish();
        });
        window.addEventListener('beforeunload', showLeoLoaderForNavigation);
        window.addEventListener('pageshow', () =>
        {
            finishLeoLoaderForNavigation();
        });
        window.addEventListener('load', () =>
        {
            finishLeoLoaderForNavigation();
        });
        window.addEventListener('leo-loader-show', showLeoLoaderForNavigation);
        window.addEventListener('leo-loader-finish', finishLeoLoaderForNavigation);
        window.addEventListener('leo-loader-hide', hideLeoLoaderForNavigation);
        const origPush = window.history.pushState;
        window.history.pushState = function()
        {
            origPush.apply(this, arguments);
            showLeoLoaderForNavigation();
            scheduleLeoLoaderFinish();
            window.dispatchEvent(new PopStateEvent('popstate'));
        };
        return () =>
        {
            window.removeEventListener('popstate', onNav);
            window.removeEventListener('beforeunload', showLeoLoaderForNavigation);
            window.removeEventListener('pageshow', hideLeoLoaderForNavigation);
            window.removeEventListener('load', hideLeoLoaderForNavigation);
            window.removeEventListener('leo-loader-show', showLeoLoaderForNavigation);
            window.removeEventListener('leo-loader-hide', hideLeoLoaderForNavigation);
            window.history.pushState = origPush;
        };
    }, [setLeoLoaderActivity]);

    // react to path changes
    useEffect(() =>
    {
        if (isMainThemeRoute(currentPath))
        {
            const from = (window.history && window.history.state && window.history.state.from) || null;

            // coming back from /discord should preserve the current SpaceDots state and keep music playing
            if (from === '/discord')
            {
                if (!mountSpace) setMountSpace(true);
                setHeaderMounted(false);
                setAriMounted(false);
                setHeaderVisible(false);
                setAriVisible(false);
                return;
            }

            // /main uses the "pass through space" dot motion.
            setSpaceMotion('centerOut');
            if (skipMainHideRef.current)
            {
                // special condition: return from RGL — restore background and show only /main UI
                skipMainHideRef.current = false;
                if (!mountSpace) setMountSpace(true);
                // wait a frame then start fade-in
                requestAnimationFrame(() => requestAnimationFrame(() => setFadeStarted(true)));
                // ensure header and Ari are NOT shown cuz /main has its own UI
                setHeaderMounted(false);
                setAriMounted(false);
                setHeaderVisible(false);
                setAriVisible(false);
                return;
            }
            // entering main: fade out header, fade audio out, then unmount
            setHeaderVisible(false);
            // fade out Ari then unmount after same delay as header
            setAriVisible(false);
            // start audio fade-out when the UI is called to fade out
            fadeOutAndStopAudio(420);
            // allow the fade to finish then unmount
            const t = setTimeout(() => setHeaderMounted(false), 500);
            setTimeout(() => setAriMounted(false), 520); // notice ari lags a bit behind, what a guy
            return () => clearTimeout(t);
        }
        else if (currentPath === '/')
        {
            // root uses the legacy drifting-down dots by default
            setSpaceMotion('fall');
            // returning to rootn
            setHeaderMounted(true);
            setAriMounted(true);
            // small timeout to allow mount before fade in
            const t = setTimeout(() =>
            {
                if (introDone) setHeaderVisible(true);
            }, 40);
            setTimeout(() =>
            {
                if (introDone) setAriVisible(true);
            }, 60);
            // ensure space mounted and preload webaudio so playback can start quickly
            if (!mountSpace) setMountSpace(true);
            try
            {
                preloadWebAudio();
            }
            catch (e)
            {}
            // trigger the UI fade only if the intro has already completed
            if (introDone)
            {
                requestAnimationFrame(() => requestAnimationFrame(() => setFadeStarted(true)));
            }
            return () => clearTimeout(t);
        }
        else if (currentPath === '/discord' || currentPath === '/AboutMe' || currentPath === '/Projects' || currentPath === '/ClickTheIdiot' || currentPath === '/settings')
        {
            // Preserve SpaceDots exactly as they currently are; only ensure root-only UI stays hidden.
            if (!mountSpace) setMountSpace(true);
            setSpaceMotion(currentPath === '/ClickTheIdiot' ? 'centerOut' : 'fall');
            setHeaderVisible(false);
            setAriVisible(false);
            setHeaderMounted(false);
            setAriMounted(false);
        }
        else if (currentPath === '/SpotTheFloof')
        {
            setSpaceMotion('fall');
            setHeaderVisible(false);
            setAriVisible(false);
            fadeOutAndStopAudio(420);
            setFadeStarted(false);
            setMountSpace(false);
            setHeaderMounted(false);
            setAriMounted(false);
        }
        else if (isRglRoute(currentPath))
        {
            setSpaceMotion('fall');
            // entering the RGL sequence: hide all UI, fade audio, and remove background
            setHeaderVisible(false);
            setAriVisible(false);
            fadeOutAndStopAudio(420);
            // fade the space/background out then unmount it
            setFadeStarted(false);
            setTimeout(() => setMountSpace(false), SPACE_FADE_MS);
            setHeaderMounted(false);
            setAriMounted(false);
        }
        else if (currentPath === '/FR*nc' || currentPath === '/PCUplaybook')
        {
            setHeaderVisible(false);
            setAriVisible(false);
            fadeOutAndStopAudio(420);
            setFadeStarted(false);
            setMountSpace(false);
            setHeaderMounted(false);
            setAriMounted(false);
        }
        else
        {
            setSpaceMotion('fall');
            // any other subpage ensure header + Ari are not visible or mounted
            setHeaderVisible(false);
            setAriVisible(false);
            setHeaderMounted(false);
            setAriMounted(false);
        }
    }, [currentPath, getOrCreateAudio, fadeOutAndStopAudio, preloadWebAudio, introDone, mountSpace]);

    // play or stop/fade audio when header becomes visible/invisible
    useEffect(() =>
    {
        // awful shit right here, im loosing my mind #2
    }, [headerVisible]);

    // when the visual fade starts, only auto-play loop on the root UI (/)
    // not on subpages. Subpages should never start loop on fade other wise im killing myself
    useEffect(() =>
    {
        if (!fadeStarted) return;
        if (currentPath === '/' && !playedSinceFadeRef.current)
        {
            playLoopImmediate();
        }
    }, [fadeStarted, currentPath, playLoopImmediate]);

    // ensure that when header shows again (UI being called to fade back in),
    // we play the loop from the start if we haven't already for this cycle.
    useEffect(() =>
    {
        if (headerVisible)
        {
            // only start when UI visual has already been enabled (not during intro bc shit kept breaking on this condition)
            if (fadeStarted && !playedSinceFadeRef.current)
            {
                playLoopImmediate();
            }
        }
        else
        {
            // header hidden — mark that we need to replay when it next shows
            playedSinceFadeRef.current = false;
        }
    }, [headerVisible, fadeStarted, playLoopImmediate]);

    // preload audio during fade so it can start immediately after intro finishes
    const handleOverlayFadeStart = () =>
    {
        if (fadeStarted) return;
        // AA
        if (!mountSpace) setMountSpace(true);
        // preload audio but don't play yet
        try
        {
            getOrCreateAudio();
            try
            {
                preloadWebAudio();
            }
            catch (e)
            {}
        }
        catch (e)
        {}
    };

    // used to request overlay to try playback under a user gesture, whatever that does
    const [playRequestedKey, setPlayRequestedKey] = useState(0);

    const handleIntroComplete = useCallback(() =>
    {
        // remove shit by IntroOverlay
        setIntroDone(true);
        // no playback here. playback will begin when the UI visual
        // fade starts! the loop does not play while the intro video is visible

        // I FUCKING FORGOT TO MOUNT THIS SHIT
        if (!mountSpace) setMountSpace(true);
        // trigger the visual fade on the next frame (two rAFs to ensure CSS transition)
        requestAnimationFrame(() => requestAnimationFrame(() =>
        {
            setFadeStarted(true);
            // start playback immediately when the UI fade is triggered here
            playLoopImmediate();
        }));
        // short delay then fade in header UI so space finishes fading in first
        setTimeout(() => setHeaderVisible(true), HEADER_DELAY_MS);
    }, [mountSpace, playLoopImmediate]);

    const handleEnableMedia = async () =>
    {
        setEnableError('');
        try
        {
            allowPlayOnFadeRef.current = true;
            setPlayRequestedKey((k) => k + 1);
            if (fadeStarted && headerVisible)
            {
                // prefer WebAudio start if its available
                try
                {
                    if (webLoadedRef.current && audioCtxRef.current)
                    {
                        try
                        {
                            if (audioCtxRef.current.state === 'suspended') await audioCtxRef.current.resume();
                        }
                        catch (e)
                        {}
                        startWebAudioLoop();
                        try
                        {
                            tryStartHeartBeatFromWebAudio();
                        }
                        catch (e)
                        {}
                        setAutoplayBlocked(false);
                        if (!videoAutoplayBlocked) setRequireEnable(false);
                        return;
                    }
                }
                catch (e)
                {}
                const audio = getOrCreateAudio();
                // try to play main + hover tracks under the user gesture
                const toPlay = [];
                const perfStart = (typeof performance !== 'undefined' && performance.now) ? (performance.now() / 1000) : (Date.now() / 1000);
                try
                {
                    toPlay.push(audio.play && audio.play());
                }
                catch (e)
                {}
                if (hover1Ref.current) try
                {
                    toPlay.push(hover1Ref.current.play && hover1Ref.current.play());
                }
                catch (e)
                {}
                if (hover2Ref.current) try
                {
                    toPlay.push(hover2Ref.current.play && hover2Ref.current.play());
                }
                catch (e)
                {}
                try
                {
                    await Promise.allSettled(toPlay);
                }
                catch (e)
                {}
                try
                {
                    startHeartBeat(() => ((typeof performance !== 'undefined' && performance.now) ? (performance.now() / 1000) : (Date.now() / 1000)), perfStart, heartBeatStageRef.current || 'base');
                }
                catch (e)
                {}
                // mute state is not tracked in the UI
                setAutoplayBlocked(false);
                if (!videoAutoplayBlocked) setRequireEnable(false);
            }
        }
        catch (err)
        {
            console.error('Enable media failed', err);
            setEnableError('Playback failed. Please allow audio/video and try again.');
            setRequireEnable(true);
        }
    };

    // if we're on a mobile device, skip the intro video entirely and
    // immediately mount the space UI, start the fade transition, and
    // attempt to start the loop audio (this will still respect autoplay
    // policies and surface the enable modal if blocked).
    // support for mobile will have to wait
    useEffect(() =>
    {
        let cancelled = false;

        const bootstrapMobileRoot = async () =>
        {
            try
            {
                if (!shouldSkipRootIntroOnMobile) return;
                // avoid double-running if the intro was already completed for some reason
                if (introDone) return;

                // create the HTMLAudio elements and preload WebAudio first so the hover tracks stay aligned.
                try
                {
                    getOrCreateAudio();
                }
                catch (e)
                {}
                try
                {
                    await preloadWebAudio();
                }
                catch (e)
                {}
                if (cancelled) return;

                if (!mountSpace) setMountSpace(true);
                handleIntroComplete();
            }
            catch (e)
            {
                // swallow any unexpected errors here — nothing fatal for desktop flow
                console.warn('Mobile skip intro failed, your fucking device sucks ass (unsupported)', e);
            }
        };

        bootstrapMobileRoot();

        return () =>
        {
            cancelled = true;
        };
        // intentionally run once on mount
        // awful shit right here, im loosing my mind
        // i dont think im working on mobile support
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [introDone, mountSpace, handleIntroComplete, getOrCreateAudio, preloadWebAudio, shouldSkipRootIntroOnMobile]);

    // checks for subpage (not "/"), if subpage then skip the intro completely and
    // bring the background in immediately.

    // for some reason this fucking broke while adding new audio files and changing transition sequence
    // this code can be so fucking lame for no reason
    useEffect(() =>
    {
        const path = normalizePath(window.location.pathname || '/');
        if (path === '/') return; // keep intro on home
        // skip intro on subpages, should only be played on root page
        setIntroDone(true);
        if (isRglRoute(path) || path === '/SpotTheFloof')
        {
            setMountSpace(false);
            setFadeStarted(false);
            setHeaderMounted(false);
            setHeaderVisible(false);
            setAriMounted(false);
            setAriVisible(false);
            return;
        }
        setMountSpace(true);
        requestAnimationFrame(() => requestAnimationFrame(() => setFadeStarted(true)));
        if (isMainThemeRoute(path))
        {
            setHeaderMounted(false);
            setHeaderVisible(false);
        }
        // run once on mount
    }, []);

    // hover audio control for the Start button: raise hover tracks to full, should be scaled by master volume
    // Removed legacy hover helpers; replaced by setHoverTrackGain and proximity logic

    const spaceDotsRef = useRef(null);
    const originalSpaceSpeedRef = useRef(0.6); // matches SpaceDots speedFactor
    const navigatingRef = useRef(false);
    const rglPreloadRef = useRef(
    {});
    const [rglDirectEntryReady, setRglDirectEntryReady] = useState(() => !isRglRoute(initialPathRef.current));
    const skipMainHideRef = useRef(false);
    const startButtonRef = useRef(null);
    const ringHoverActiveRef = useRef(false);

    const waitForVideoReady = useCallback((video) =>
    {
        if (!video) return Promise.resolve();
        if (video.readyState >= 3) return Promise.resolve();

        return new Promise((resolve) =>
        {
            let settled = false;
            let timeoutId = null;

            const finish = () =>
            {
                if (settled) return;
                settled = true;
                try
                {
                    video.removeEventListener('canplaythrough', finish);
                }
                catch (e)
                {}
                try
                {
                    video.removeEventListener('loadeddata', finish);
                }
                catch (e)
                {}
                try
                {
                    video.removeEventListener('error', finish);
                }
                catch (e)
                {}
                if (timeoutId) clearTimeout(timeoutId);
                resolve();
            };

            try
            {
                video.addEventListener('canplaythrough', finish);
            }
            catch (e)
            {}
            try
            {
                video.addEventListener('loadeddata', finish);
            }
            catch (e)
            {}
            try
            {
                video.addEventListener('error', finish);
            }
            catch (e)
            {}
            timeoutId = setTimeout(finish, 5000);
            try
            {
                video.load();
            }
            catch (e)
            {
                finish();
            }
        });
    }, []);

    const preloadRGLAssets = useCallback(async (
    {
        waitForVideos = false
    } = {}) =>
    {
        setLeoLoaderActivity(true);
        const p = rglPreloadRef.current ||
        {};

        if (!p.close)
        {
            const vClose = document.createElement('video');
            vClose.preload = 'auto';
            vClose.src = '/Video/TF2_CompDoors-CLOSE.mp4';
            try
            {
                vClose._baseVolume = 1.0;
            }
            catch (e)
            {}
            try
            {
                masterVolume.applyToElement(vClose);
            }
            catch (e)
            {}
            try
            {
                vClose.load();
            }
            catch (e)
            {}
            p.close = vClose;
        }

        if (!p.open)
        {
            const vOpen = document.createElement('video');
            vOpen.preload = 'auto';
            vOpen.src = '/Video/TF2_CompDoors-OPEN.mp4';
            try
            {
                vOpen._baseVolume = 1.0;
            }
            catch (e)
            {}
            try
            {
                masterVolume.applyToElement(vOpen);
            }
            catch (e)
            {}
            try
            {
                vOpen.load();
            }
            catch (e)
            {}
            p.open = vOpen;
        }

        if (!p.icon)
        {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = '/RGL-Icon.png';
            p.icon = img;
        }

        if (!p.iconXML && !p.iconXMLPromise)
        {
            p.iconXMLPromise = fetch('/RGL-Icon.xml')
                .then((r) => r.text())
                .then((txt) =>
                {
                    p.iconXML = txt;
                    return txt;
                })
                .catch(() => null)
                .finally(() =>
                {
                    try
                    {
                        delete p.iconXMLPromise;
                    }
                    catch (e)
                    {}
                });
        }

        rglPreloadRef.current = p;

        try
        {
            if (waitForVideos)
            {
                await Promise.all([
                    waitForVideoReady(p.close),
                    waitForVideoReady(p.open),
                ]);
            }

            return p;
        }
        finally
        {
            setLeoLoaderActivity(false);
        }
    }, [setLeoLoaderActivity, waitForVideoReady]);

    const setHoverTrackGain = useCallback((track, toGain, durationMs = 600) =>
    {
        try
        {
            const ctx = audioCtxRef.current;
            const gains = webGainsRef.current;
            const clamp01 = (v) => Math.max(0, Math.min(1, v));
            const target = 1 * clamp01(toGain);
            if (ctx && gains && gains[track])
            {
                try
                {
                    const now = ctx.currentTime;
                    const g = gains[track].gain;
                    g.cancelScheduledValues(now);
                    g.setValueAtTime(g.value || 0, now);
                    g.linearRampToValueAtTime(target, now + durationMs / 1000);
                    return;
                }
                catch (e)
                {}
            }
            const el = track === 'h1' ? hover1Ref.current : hover2Ref.current;
            if (!el) return;
            try
            {
                if (el._hoverGainRaf) cancelAnimationFrame(el._hoverGainRaf);
            }
            catch (e)
            {}
            const start = performance.now();
            const from = typeof el._hoverGain === 'number' ? el._hoverGain : 0;
            const step = (now) =>
            {
                const t = Math.min(1, (now - start) / durationMs);
                const v = from + (clamp01(toGain) - from) * t;
                el._hoverGain = v;
                try
                {
                    const m = masterVolume.getMasterVolume();
                    el.volume = Math.max(0, Math.min(1, (el._baseVolume || 1.0) * m * v));
                }
                catch (e)
                {}
                if (t < 1)
                {
                    el._hoverGainRaf = requestAnimationFrame(step);
                }
                else
                {
                    el._hoverGainRaf = null;
                    if (v <= 0)
                    {
                        try
                        {
                            el.volume = 0;
                        }
                        catch (e)
                        {}
                    }
                }
            };
            el._hoverGainRaf = requestAnimationFrame(step);
        }
        catch (e)
        {}
    }, []);

    useEffect(() =>
    {
        let cancelled = false;

        if (!isRglRoute(currentPath) || rglDirectEntryReady) return undefined;

        preloadRGLAssets(
            {
                waitForVideos: true
            })
            .catch(() =>
            {})
            .finally(() =>
            {
                if (!cancelled) setRglDirectEntryReady(true);
            });

        return () =>
        {
            cancelled = true;
        };
    }, [currentPath, rglDirectEntryReady, preloadRGLAssets]);

    // (moved) RGL trigger listener is registered after handleRGLSequence is defined

    // listen for RGL open-complete to restore UI and navigate back to /main
    useEffect(() =>
    {
        const onOpenFinished = () =>
        {
            // mark that we should NOT run the default '/main' hide behavior
            skipMainHideRef.current = true;
            // navigate to /main and useEffect for currentPath will consult skipMainHideRef
            navigate('/main');
        };
        window.addEventListener('rgl-open-finished', onOpenFinished);
        return () => window.removeEventListener('rgl-open-finished', onOpenFinished);
    }, []);

    const handleStartSequence = async () =>
    {
        if (navigatingRef.current) return; // prevent overlapping sequences
        navigatingRef.current = true;
        try
        {
            // kill start-button hover audio immediately
            try
            {
                setHoverTrackGain('h1', 0.0, 120);
            }
            catch (e)
            {}
            try
            {
                setHoverTrackGain('h2', 0.0, 120);
            }
            catch (e)
            {}

            // switch dots into "pass through" mode for the transition
            setSpaceMotion('centerOut');
            // hide header immediately (fade handled by opacity transition already)
            setHeaderVisible(false);
            // hide Ari
            setAriVisible(false);
            // fire audio fade-out in parallel
            fadeOutAndStopAudio(420);

            // play entering main SFX once (independent of loop) if user allowed playback
            try
            {
                const preloadedHalloweenAudio = HALLOWEEN_MODE && window.__aw24HalloweenEntryAudio;
                const sfx = preloadedHalloweenAudio && typeof preloadedHalloweenAudio.cloneNode === 'function' ?
                    preloadedHalloweenAudio.cloneNode(true) :
                    new Audio(HALLOWEEN_MODE ? HALLOWEEN_ENTRY_AUDIO_SRC : '/Audio/EnteringMain.ogg');
                sfx._baseVolume = 1.0;
                try
                {
                    sfx.volume = sfx._baseVolume * masterVolume.getMasterVolume();
                }
                catch (e)
                {}
                try
                {
                    masterVolume.applyToElement(sfx);
                }
                catch (e)
                {}
                sfx.play().catch(err =>
                {
                    // try mp3 fallback, will prob remove this later
                    try
                    {
                        sfx.src = '/Audio/EnteringMain.mp3';
                        sfx.play().catch(() =>
                        {});
                    }
                    catch (e)
                    {}
                });
            }
            catch (e)
            {
                /* ignore */ }
            // accelerate stars then decelerate
            const space = spaceDotsRef.current;
            if (space && typeof space.pulseSpeed === 'function')
            {
                // accelerate stars then decelerate (same timing, slower end speed)
                const slowTarget = .2;
                // ramp up
                if (typeof space.animateSpeed === 'function')
                {
                    await new Promise((resolve) =>
                    {
                        try
                        {
                            space.animateSpeed(50, HALLOWEEN_MODE ? HALLOWEEN_ENTRY_SPEED_UP_MS : MAIN_ENTRY_SPEED_UP_MS,
                            {
                                easing: 'easeInQuad',
                                onDone: resolve
                            });
                        }
                        catch (e)
                        {
                            resolve();
                        }
                    });
                    // ramp down to very slow
                    await new Promise((resolve) =>
                    {
                        try
                        {
                            space.animateSpeed(slowTarget, 550,
                            {
                                easing: 'easeOutQuad',
                                onDone: resolve
                            });
                        }
                        catch (e)
                        {
                            resolve();
                        }
                    });
                }
                else
                {
                    // fallback to legacy behavior if animateSpeed isn't available
                    await space.pulseSpeed(50, HALLOWEEN_MODE ? HALLOWEEN_ENTRY_SPEED_UP_MS : MAIN_ENTRY_SPEED_UP_MS, 550);
                }
            }
            // after sequence, unmount Ari to avoid interaction on /main
            // this shit should've been done earlier god damn it
            setAriMounted(false);
            // after sequence navigate
            navigate('/main');
        }
        catch (e)
        {
            console.warn('Start sequence failed', e);
            navigate('/main');
        }
        finally // this fucntion is so annoying jesus christ
        {
            navigatingRef.current = false;
        }
    };

    const handleRGLSequence = useCallback(async () =>
    {
        if (navigatingRef.current) return;
        navigatingRef.current = true;
        try
        {
            try
            {
                preloadRGLAssets();
            }
            catch (e)
            {}

            // hide header and ari immediately
            setHeaderVisible(false);
            setAriVisible(false);
            // start audio fade
            fadeOutAndStopAudio(420);
            // trigger space/background fade out
            setFadeStarted(false);
            // wait for the fade to complete then unmount background and navigate
            setTimeout(() =>
            {
                setMountSpace(false);
                setHeaderMounted(false);
                setAriMounted(false);
                navigate('/RGL');
            }, SPACE_FADE_MS);
        }
        catch (e)
        {
            console.warn('RGL sequence failed', e);
            navigate('/RGL');
        }
        finally
        {
            // unlock after a short grace (do not block forever)
            setTimeout(() =>
            {
                navigatingRef.current = false;
            }, SPACE_FADE_MS);
        }
    }, [fadeOutAndStopAudio, preloadRGLAssets]);

    // listen for RGL trigger event fired from other components
    useEffect(() =>
    {
        const onEnter = () =>
        {
            handleRGLSequence();
        };
        window.addEventListener('enter-rgl', onEnter);
        return () => window.removeEventListener('enter-rgl', onEnter);
    }, [handleRGLSequence]);

    // Proximity detection for hover1 based on visible circle radius
    useEffect(() =>
    {
        if (!(headerVisible && currentPath === '/')) return;
        const onMove = (e) =>
        {
            try
            {
                const btn = startButtonRef.current;
                if (!btn) return;
                const rect = btn.getBoundingClientRect();
                const cx = rect.left + rect.width / 2;
                const cy = rect.top + rect.height / 2;
                const dx = e.clientX - cx;
                const dy = e.clientY - cy;
                const dist = Math.hypot(dx, dy);
                let radius = 60;
                try
                {
                    const cs = getComputedStyle(btn, '::after');
                    const w = parseFloat(cs.width) || 0; // base diameter without transform
                    const tf = cs.transform || 'none';
                    let scale = 1;
                    if (tf && tf !== 'none')
                    {
                        const m = tf.match(/matrix\(([^)]+)\)/);
                        if (m)
                        {
                            const parts = m[1].split(',').map(s => parseFloat(s.trim()));
                            if (parts.length >= 6) scale = Math.abs(parts[0]) || 1; // a component
                        }
                    }
                    radius = (w / 2) * scale;
                }
                catch (err)
                {}
                const inside = dist <= radius;
                if (inside !== ringHoverActiveRef.current)
                {
                    ringHoverActiveRef.current = inside;
                    if (inside)
                    {
                        // hover1 stage: special 16/4 pattern, synced to same clock start
                        try
                        {
                            setHeartBeatStage('hover1');
                        }
                        catch (e)
                        {}
                        setHoverTrackGain('h1', 1.0, 600);
                    }
                    else
                    {
                        // base stage: downbeat-only every 16
                        try
                        {
                            setHeartBeatStage('base');
                        }
                        catch (e)
                        {}
                        setHoverTrackGain('h1', 0.0, 600);
                    }
                }
            }
            catch (err)
            {}
        };
        window.addEventListener('mousemove', onMove);
        return () =>
        {
            window.removeEventListener('mousemove', onMove);
            ringHoverActiveRef.current = false;
            try
            {
                setHeartBeatStage('base');
            }
            catch (e)
            {}
            try
            {
                setHoverTrackGain('h1', 0.0, 200);
            }
            catch (e)
            {}
        };
    }, [headerVisible, currentPath, setHoverTrackGain, setHeartBeatStage]);

    const mainAudioRef = useRef(null);
    const mainDesiredSrcRef = useRef(null);
    const mainIntroSrcRef = useRef(null);
    const mainBaseVolumeRef = useRef(0.5);
    const mainStartDelayMsRef = useRef(0);
    const mainOnBeatEntryRef = useRef(null);
    const mainRestartFromIntroRef = useRef(false);
    const mainIntroPlayedRef = useRef(false);
    const mainIntroAudioRef = useRef(null);
    const mainIntroEndedHandlerRef = useRef(null);
    const mainStartDelayTimerRef = useRef(null);
    const mainBeatEntryTimerRef = useRef(null);
    const mainFadeRafRef = useRef(null);
    const mainPreloadPromisesRef = useRef(
    {});
    const mainLoopPrimedSrcRef = useRef(null);
    const mainSelectedClassNameRef = useRef('Default');
    const mainSelectionRouteRef = useRef(null);
    const mainSelectionVersionRef = useRef(0);
    const mainQueuedSelectionVersionRef = useRef(0);
    const mainStartAttemptRef = useRef(0);
    const mainBeatTimelineRef = useRef(
    {
        startMs: null,
        bpm: null,
        targetBeat: null,
        loopStarted: false
    });

    const clearPendingMainStart = useCallback(() =>
    {
        if (mainStartDelayTimerRef.current)
        {
            clearTimeout(mainStartDelayTimerRef.current);
            mainStartDelayTimerRef.current = null;
        }
        if (mainBeatEntryTimerRef.current)
        {
            clearTimeout(mainBeatEntryTimerRef.current);
            mainBeatEntryTimerRef.current = null;
        }
    }, []);

    const refreshMainBeatStateBeforeStart = useCallback(() =>
    {
        if (mainBeatEntryTimerRef.current)
        {
            clearTimeout(mainBeatEntryTimerRef.current);
            mainBeatEntryTimerRef.current = null;
        }

        const onBeatEntry = mainOnBeatEntryRef.current;
        mainBeatTimelineRef.current = {
            startMs: null,
            bpm: onBeatEntry ? onBeatEntry.bpm : null,
            targetBeat: onBeatEntry ? onBeatEntry.beats : null,
            loopStarted: false,
        };
        mainLoopPrimedSrcRef.current = null;

        if (mainAudioRef.current)
        {
            try
            {
                mainAudioRef.current.pause();
            }
            catch (e)
            {}
            try
            {
                mainAudioRef.current.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                mainAudioRef.current.muted = false;
            }
            catch (e)
            {}
        }

        if (mainIntroAudioRef.current)
        {
            try
            {
                const handler = mainIntroEndedHandlerRef.current;
                if (handler) mainIntroAudioRef.current.removeEventListener('ended', handler);
            }
            catch (e)
            {}
            try
            {
                mainIntroAudioRef.current.pause();
            }
            catch (e)
            {}
            try
            {
                mainIntroAudioRef.current.currentTime = 0;
            }
            catch (e)
            {}
            mainIntroEndedHandlerRef.current = null;
        }
    }, []);

    useEffect(() =>
    {
        const onKeyDown = (e) =>
        {
            if (!e.shiftKey || e.repeat) return;
            if ((e.key || '').toLowerCase() !== 'b') return;

            const target = e.target;
            const tag = target && target.tagName ? target.tagName.toLowerCase() : '';
            if (tag === 'input' || tag === 'textarea' || (target && target.isContentEditable)) return;

            e.preventDefault();
            setShowMainBpmDebug((prev) =>
            {
                const next = !prev;
                try
                {
                    localStorage.setItem(MAIN_BPM_DEBUG_STORAGE_KEY, next ? '1' : '0');
                }
                catch (err)
                {}
                return next;
            });
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() =>
    {
        if (!showMainBpmDebug || !isMainThemeRoute(currentPath)) return;

        const updateDebugSnapshot = () =>
        {
            const onBeatEntry = mainOnBeatEntryRef.current;
            const timeline = mainBeatTimelineRef.current ||
            {};
            let currentBeat = null;

            if (onBeatEntry && Number.isFinite(timeline.startMs))
            {
                const elapsedMs = Math.max(0, performance.now() - timeline.startMs);
                const msPerBeat = 60000 / onBeatEntry.bpm;
                currentBeat = Math.max(1, Math.floor(elapsedMs / msPerBeat) + 1);
            }

            setMainBpmDebugSnapshot(
            {
                className: mainSelectedClassNameRef.current || 'Default',
                enabled: !!onBeatEntry,
                bpm: onBeatEntry ? onBeatEntry.bpm : null,
                currentBeat,
                targetBeat: onBeatEntry ? onBeatEntry.beats : null,
                loopStarted: !!timeline.loopStarted,
            });
        };

        updateDebugSnapshot();
        const intervalId = setInterval(updateDebugSnapshot, 50);
        return () => clearInterval(intervalId);
    }, [currentPath, showMainBpmDebug]);

    const preloadMainClassAsset = useCallback((src) =>
    {
        if (!src) return null;

        const existing = mainPreloadPromisesRef.current[src];
        if (existing) return existing;

        const preloadPromise = fetch(src,
            {
                cache: 'force-cache'
            })
            .then((res) =>
            {
                if (!res.ok) throw new Error('Failed to preload audio: ' + src);
                return res.arrayBuffer();
            })
            .catch((err) =>
            {
                console.warn('Failed to preload /main audio asset', src, err);
                delete mainPreloadPromisesRef.current[src];
                return null;
            });

        mainPreloadPromisesRef.current[src] = preloadPromise;
        return preloadPromise;
    }, []);

    const preloadMainClassAudio = useCallback((detail) =>
    {
        try
        {
            const loopSrc = detail && detail.music ? detail.music : null;
            const introSrc = detail && detail.onBeatIntro === false ? null : (detail && detail.intro ? detail.intro : null);
            const baseVol = getDetailBaseVolume(detail, 0.5);

            try
            {
                const preloadJobs = [
                    preloadMainClassAsset(loopSrc),
                    preloadMainClassAsset(introSrc),
                ].filter(Boolean);
                if (preloadJobs.length) Promise.all(preloadJobs).catch(() =>
                {});
            }
            catch (e)
            {}

            // Preload main loop element

            // Self Note: add all class audio files to be in volume control bar

            if (loopSrc)
            {
                try
                {
                    if (!mainAudioRef.current)
                    {
                        mainAudioRef.current = createAudioElement(loopSrc, baseVol, true);
                    }
                    else
                    {
                        const a = mainAudioRef.current;
                        if (!(a.src || '').includes(loopSrc))
                        {
                            a.src = loopSrc;
                        }
                    }
                    const a = mainAudioRef.current;
                    a.loop = true;
                    a._baseVolume = baseVol;
                    try
                    {
                        masterVolume.applyToElement(a);
                    }
                    catch (e)
                    {}
                    a.preload = 'auto';
                    try
                    {
                        a.load();
                    }
                    catch (e)
                    {}
                }
                catch (e)
                {}
            }

            // Preload intro element (one-shot)
            if (introSrc)
            {
                try
                {
                    // detach any previous ended handler to avoid duplicates
                    if (mainIntroAudioRef.current && mainIntroEndedHandlerRef.current)
                    {
                        try
                        {
                            mainIntroAudioRef.current.removeEventListener('ended', mainIntroEndedHandlerRef.current);
                        }
                        catch (e)
                        {}
                        mainIntroEndedHandlerRef.current = null;
                    }
                    if (!mainIntroAudioRef.current)
                    {
                        mainIntroAudioRef.current = createAudioElement(introSrc, baseVol, true);
                    }
                    else
                    {
                        const ia = mainIntroAudioRef.current;
                        if (!(ia.src || '').includes(introSrc))
                        {
                            ia.src = introSrc;
                        }
                    }
                    const ia = mainIntroAudioRef.current;
                    ia.loop = false;
                    ia._baseVolume = baseVol;
                    try
                    {
                        masterVolume.applyToElement(ia);
                    }
                    catch (e)
                    {}
                    ia.preload = 'auto';
                    try
                    {
                        ia.load();
                    }
                    catch (e)
                    {}
                }
                catch (e)
                {}
            }
        }
        catch (e)
        {}
    }, [createAudioElement, preloadMainClassAsset]);

    const primeMainLoopForBeatEntry = useCallback(async () =>
    {
        const desiredSrc = mainDesiredSrcRef.current;
        if (!desiredSrc) return;
        if (mainLoopPrimedSrcRef.current === desiredSrc) return;

        try
        {
            if (!mainAudioRef.current)
            {
                mainAudioRef.current = createAudioElement(desiredSrc, mainBaseVolumeRef.current, true);
            }

            const mainAudio = mainAudioRef.current;
            if (!mainAudio) return;

            if (mainAudio.src && !mainAudio.src.includes(desiredSrc))
            {
                mainAudio.src = desiredSrc;
            }

            mainAudio.loop = true;
            mainAudio._baseVolume = Number.isFinite(mainBaseVolumeRef.current) ? clamp01(mainBaseVolumeRef.current) : 0.5;
            try
            {
                masterVolume.applyToElement(mainAudio);
            }
            catch (e)
            {}
            try
            {
                mainAudio.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                mainAudio.muted = true;
            }
            catch (e)
            {}
            await mainAudio.play();
            try
            {
                mainAudio.pause();
            }
            catch (e)
            {}
            try
            {
                mainAudio.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                mainAudio.muted = false;
            }
            catch (e)
            {}
            mainLoopPrimedSrcRef.current = desiredSrc;
        }
        catch (err)
        {
            try
            {
                if (mainAudioRef.current) mainAudioRef.current.muted = false;
            }
            catch (e)
            {}
            console.warn('Failed to prime main loop for beat entry', err);
            mainLoopPrimedSrcRef.current = null;
        }
    }, [createAudioElement]);

    const playMainLoop = useCallback(async () =>
    {
        // Only play when a class-selected source is available.
        let desiredSrc = mainDesiredSrcRef.current;
        if (!desiredSrc) return;

        if (!mainAudioRef.current)
        {
            mainAudioRef.current = createAudioElement(desiredSrc, mainBaseVolumeRef.current, true);
        }
        const mainAudio = mainAudioRef.current;
        // if audio already exists but src differs from desired, switch to desired
        try
        {
            if (mainAudio && mainAudio.src && !mainAudio.src.includes(desiredSrc))
            {
                mainAudio.src = desiredSrc;
                try
                {
                    mainAudio.currentTime = 0;
                }
                catch (e)
                {}
            }
        }
        catch (e)
        {}
        try
        {
            // set per-file base volume and apply master multiplier
            mainAudio._baseVolume = Number.isFinite(mainBaseVolumeRef.current) ? clamp01(mainBaseVolumeRef.current) : 0.5;
            try
            {
                masterVolume.applyToElement(mainAudio);
            }
            catch (e)
            {}
            try
            {
                mainAudio.muted = false;
            }
            catch (e)
            {}
            mainAudio.currentTime = 0;
            await mainAudio.play();
            mainLoopPrimedSrcRef.current = desiredSrc;
            if (mainOnBeatEntryRef.current)
            {
                const existingStartMs = mainBeatTimelineRef.current && Number.isFinite(mainBeatTimelineRef.current.startMs) ?
                    mainBeatTimelineRef.current.startMs :
                    performance.now();
                mainBeatTimelineRef.current = {
                    startMs: existingStartMs,
                    bpm: mainOnBeatEntryRef.current.bpm,
                    targetBeat: mainOnBeatEntryRef.current.beats,
                    loopStarted: true,
                };
            }
        }
        catch (err)
        {
            console.warn('Failed to play main loop', err);
        }
    }, [createAudioElement]);

    const stopMainLoop = useCallback(() =>
    {
        clearPendingMainStart();
        mainStartAttemptRef.current += 1;
        if (mainFadeRafRef.current)
        {
            try
            {
                cancelAnimationFrame(mainFadeRafRef.current);
            }
            catch (e)
            {}
            mainFadeRafRef.current = null;
        }
        if (mainAudioRef.current)
        {
            try
            {
                mainAudioRef.current.pause();
                try
                {
                    mainAudioRef.current.muted = false;
                }
                catch (e)
                {}
                mainAudioRef.current.currentTime = 0;
                try
                {
                    if (typeof mainAudioRef.current._masterVolumeUnsub === 'function') mainAudioRef.current._masterVolumeUnsub();
                }
                catch (e)
                {}
                try
                {
                    mainAudioRef.current.src = '';
                }
                catch (e)
                {}
            }
            catch (err)
            {
                console.warn('Failed to stop Main-Loop.ogg', err);
            }
        }
        mainLoopPrimedSrcRef.current = null;
        mainBeatTimelineRef.current = {
            startMs: null,
            bpm: null,
            targetBeat: null,
            loopStarted: false
        };
        if (mainIntroAudioRef.current)
        {
            try
            {
                mainIntroAudioRef.current.pause();
            }
            catch (e)
            {}
            try
            {
                mainIntroAudioRef.current.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                if (typeof mainIntroAudioRef.current._masterVolumeUnsub === 'function') mainIntroAudioRef.current._masterVolumeUnsub();
            }
            catch (e)
            {}
            // detach ended handler if present
            try
            {
                const h = mainIntroEndedHandlerRef.current;
                if (h) mainIntroAudioRef.current.removeEventListener('ended', h);
            }
            catch (e)
            {}
            try
            {
                mainIntroAudioRef.current.src = '';
            }
            catch (e)
            {}
            mainIntroAudioRef.current = null;
            mainIntroEndedHandlerRef.current = null;
        }
    }, [clearPendingMainStart]);

    const fadeOutAndStopMainLoop = useCallback((duration = MAIN_THEME_EXIT_FADE_MS) =>
    {
        clearPendingMainStart();

        const activeAudio = [mainAudioRef.current, mainIntroAudioRef.current].filter(Boolean);
        if (!activeAudio.length)
        {
            stopMainLoop();
            return;
        }

        if (!Number.isFinite(duration) || duration <= 0)
        {
            stopMainLoop();
            return;
        }

        if (mainFadeRafRef.current)
        {
            try
            {
                cancelAnimationFrame(mainFadeRafRef.current);
            }
            catch (e)
            {}
            mainFadeRafRef.current = null;
        }

        const start = performance.now();
        const startingVolumes = activeAudio.map((audio) =>
        {
            try
            {
                return Number.isFinite(audio.volume) ? audio.volume : 1;
            }
            catch (e)
            {
                return 1;
            }
        });

        const step = (now) =>
        {
            const progress = Math.min(1, (now - start) / duration);

            activeAudio.forEach((audio, index) =>
            {
                try
                {
                    const from = startingVolumes[index];
                    audio.volume = Math.max(0, Math.min(1, from * (1 - progress)));
                }
                catch (e)
                {}
            });

            if (progress < 1)
            {
                mainFadeRafRef.current = requestAnimationFrame(step);
                return;
            }

            mainFadeRafRef.current = null;
            stopMainLoop();
        };

        mainFadeRafRef.current = requestAnimationFrame(step);
    }, [clearPendingMainStart, stopMainLoop]);

    const startMainWithIntroIfNeeded = useCallback(async (startAttemptId = mainStartAttemptRef.current) =>
    {
        if (startAttemptId !== mainStartAttemptRef.current) return;

        refreshMainBeatStateBeforeStart();
        if (startAttemptId !== mainStartAttemptRef.current) return;

        try
        {
            const introSrc = mainIntroSrcRef.current;
            if (introSrc && !mainIntroPlayedRef.current)
            {
                const baseVol = Number.isFinite(mainBaseVolumeRef.current) ? clamp01(mainBaseVolumeRef.current) : 0.5;
                const onBeatEntry = mainOnBeatEntryRef.current;
                const isStaleStartAttempt = () => startAttemptId !== mainStartAttemptRef.current;
                // create and play intro (one-shot); transition into loop on intro end or configured beat
                const a = (mainIntroAudioRef.current && (mainIntroAudioRef.current.src || '').includes(introSrc)) ?
                    mainIntroAudioRef.current :
                    createAudioElement(introSrc, baseVol, true);
                try
                {
                    a.loop = false;
                }
                catch (e)
                {}
                try
                {
                    a._baseVolume = baseVol;
                }
                catch (e)
                {}
                try
                {
                    masterVolume.applyToElement(a);
                }
                catch (e)
                {}
                mainIntroAudioRef.current = a;
                mainIntroPlayedRef.current = true;

                let transitioned = false;
                let beatTrackingStarted = false;
                let onPlaying = null;
                const cleanupIntroState = (
                {
                    stopIntro = false,
                    clearBeatTimer = true
                } = {}) =>
                {
                    if (clearBeatTimer && mainBeatEntryTimerRef.current)
                    {
                        clearTimeout(mainBeatEntryTimerRef.current);
                        mainBeatEntryTimerRef.current = null;
                    }
                    try
                    {
                        a.removeEventListener('ended', onEnded);
                    }
                    catch (e)
                    {}
                    try
                    {
                        if (onPlaying) a.removeEventListener('playing', onPlaying);
                    }
                    catch (e)
                    {}
                    if (stopIntro)
                    {
                        try
                        {
                            a.pause();
                        }
                        catch (e)
                        {}
                        try
                        {
                            a.currentTime = 0;
                        }
                        catch (e)
                        {}
                    }
                    if (mainIntroAudioRef.current === a)
                    {
                        mainIntroAudioRef.current = null;
                    }
                    mainIntroEndedHandlerRef.current = null;
                };

                const beginBeatTracking = () =>
                {
                    if (!onBeatEntry || beatTrackingStarted || isStaleStartAttempt()) return;

                    beatTrackingStarted = true;
                    const introStartMs = performance.now();
                    mainBeatTimelineRef.current = {
                        startMs: introStartMs,
                        bpm: onBeatEntry.bpm,
                        targetBeat: onBeatEntry.beats,
                        loopStarted: false,
                    };

                    try
                    {
                        primeMainLoopForBeatEntry();
                    }
                    catch (e)
                    {}

                    const msPerBeat = 60000 / onBeatEntry.bpm;
                    const beatTargetDelayMs = Math.max(0, msPerBeat * Math.max(0, onBeatEntry.beats - 1));
                    const elapsedBeforeScheduleMs = Math.max(0, performance.now() - introStartMs);
                    const remainingBeatDelayMs = Math.max(0, beatTargetDelayMs - elapsedBeforeScheduleMs);
                    if (remainingBeatDelayMs <= 0)
                    {
                        transitionToLoop();
                        return;
                    }

                    mainBeatEntryTimerRef.current = setTimeout(() =>
                    {
                        if (isStaleStartAttempt()) return;
                        mainBeatEntryTimerRef.current = null;
                        transitionToLoop();
                    }, remainingBeatDelayMs);
                };

                const transitionToLoop = () =>
                {
                    if (transitioned || isStaleStartAttempt()) return;
                    transitioned = true;
                    try
                    {
                        playMainLoop();
                    }
                    catch (e)
                    {}
                    if (!onBeatEntry)
                    {
                        cleanupIntroState(
                        {
                            stopIntro: true
                        });
                    }
                };

                const onEnded = () =>
                {
                    if (isStaleStartAttempt()) return;
                    if (onBeatEntry)
                    {
                        cleanupIntroState(
                        {
                            stopIntro: false,
                            clearBeatTimer: false
                        });
                        return;
                    }
                    transitionToLoop();
                };
                mainIntroEndedHandlerRef.current = onEnded;
                try
                {
                    a.addEventListener('ended', onEnded);
                }
                catch (e)
                {}
                onPlaying = () =>
                {
                    beginBeatTracking();
                };
                try
                {
                    a.addEventListener('playing', onPlaying);
                }
                catch (e)
                {}
                try
                {
                    a.currentTime = 0;
                }
                catch (e)
                {}
                const playPromise = a.play();

                if (onBeatEntry)
                {
                    mainBeatTimelineRef.current = {
                        startMs: null,
                        bpm: onBeatEntry.bpm,
                        targetBeat: onBeatEntry.beats,
                        loopStarted: false,
                    };
                }

                try
                {
                    await playPromise;
                }
                catch (e)
                {
                    cleanupIntroState(
                    {
                        stopIntro: true
                    });
                    if (mainBeatEntryTimerRef.current)
                    {
                        clearTimeout(mainBeatEntryTimerRef.current);
                        mainBeatEntryTimerRef.current = null;
                    }
                    if (onBeatEntry)
                    {
                        mainBeatTimelineRef.current = {
                            startMs: null,
                            bpm: onBeatEntry.bpm,
                            targetBeat: onBeatEntry.beats,
                            loopStarted: false
                        };
                    }
                    return;
                }

                if (onBeatEntry && !beatTrackingStarted)
                {
                    beginBeatTracking();
                }
                return;
            }
        }
        catch (e)
        {}
        // no intro — start loop only if a class-selected source exists
        if (mainDesiredSrcRef.current)
        {
            if (startAttemptId !== mainStartAttemptRef.current) return;
            playMainLoop();
        }
    }, [createAudioElement, playMainLoop, primeMainLoopForBeatEntry, refreshMainBeatStateBeforeStart]);

    const queueMainStart = useCallback(() =>
    {
        clearPendingMainStart();
        const startAttemptId = mainStartAttemptRef.current + 1;
        mainStartAttemptRef.current = startAttemptId;

        const delayMs = Number.isFinite(mainStartDelayMsRef.current) ? Math.max(0, mainStartDelayMsRef.current) : 0;
        if (delayMs <= 0)
        {
            startMainWithIntroIfNeeded(startAttemptId);
            return;
        }

        mainStartDelayTimerRef.current = setTimeout(() =>
        {
            if (mainStartAttemptRef.current !== startAttemptId) return;
            mainStartDelayTimerRef.current = null;
            startMainWithIntroIfNeeded(startAttemptId);
        }, delayMs);
    }, [clearPendingMainStart, startMainWithIntroIfNeeded]);

    // listen for class selection from /main to choose music
    useEffect(() =>
    {
        const onMainClassSelected = (e) =>
        {
            try
            {
                const detail = e && e.detail ? e.detail : null;
                const selectionRoute = normalizePath((detail && detail.routePath) || window.location.pathname || '/main');
                const previousSelectionRoute = mainSelectionRouteRef.current;
                const selectionVersion = mainSelectionVersionRef.current + 1;
                const src = detail && detail.music ? detail.music : null;
                mainSelectionVersionRef.current = selectionVersion;
                mainDesiredSrcRef.current = src;
                mainSelectedClassNameRef.current = detail && detail.name ? detail.name : 'Default';
                mainSelectionRouteRef.current = selectionRoute;

                // per-class base volume, multiplied by master volume when applied
                try
                {
                    mainBaseVolumeRef.current = getDetailBaseVolume(detail, 0.5);
                }
                catch (err)
                {
                    mainBaseVolumeRef.current = 0.5;
                }
                try
                {
                    mainStartDelayMsRef.current = getDetailMainStartDelayMs(detail, 0);
                }
                catch (err)
                {
                    mainStartDelayMsRef.current = 0;
                }
                try
                {
                    mainOnBeatEntryRef.current = getDetailOnBeatEntry(detail);
                }
                catch (err)
                {
                    mainOnBeatEntryRef.current = null;
                }
                try
                {
                    mainRestartFromIntroRef.current = getDetailRestartFromIntro(detail);
                }
                catch (err)
                {
                    mainRestartFromIntroRef.current = false;
                }
                mainBeatTimelineRef.current = {
                    startMs: null,
                    bpm: mainOnBeatEntryRef.current ? mainOnBeatEntryRef.current.bpm : null,
                    targetBeat: mainOnBeatEntryRef.current ? mainOnBeatEntryRef.current.beats : null,
                    loopStarted: false,
                };
                // intro handling from selected class
                try
                {
                    const introSrc = detail && detail.onBeatIntro === false ? null : (detail && detail.intro ? detail.intro : null);
                    mainIntroSrcRef.current = introSrc;
                    const from = (window.history && window.history.state && window.history.state.from) || null;
                    const routeFrom = previousPathRef.current;
                    const shouldPlayIntro = shouldPlayMainThemeIntro(
                    {
                        nextRoute: selectionRoute,
                        from,
                        routeFrom,
                        restartFromIntro: mainRestartFromIntroRef.current,
                        previousSelectionRoute,
                    });
                    mainIntroPlayedRef.current = shouldPlayIntro ? false : true;
                }
                catch (err)
                {
                    mainIntroSrcRef.current = null;
                }

                // Preload audio elements to avoid delays or silence gaps
                try
                {
                    preloadMainClassAudio(detail);
                }
                catch (e)
                {}
                // if already on the selected main-theme route, switch to the selected track immediately
                try
                {
                    const activeRoute = normalizePath(window.location.pathname || '/');
                    if (isMainThemeRoute(activeRoute) && activeRoute === selectionRoute)
                    {
                        // If coming back from /discord, always keep the current main loop running.
                        const from = (window.history && window.history.state && window.history.state.from) || null;
                        const routeFrom = previousPathRef.current;
                        if (from !== '/discord' && routeFrom !== '/discord')
                        {
                            if (mainIntroPlayedRef.current === false || (previousSelectionRoute && previousSelectionRoute !== selectionRoute))
                            {
                                stopMainLoop();
                            }
                            mainQueuedSelectionVersionRef.current = selectionVersion;
                            // Route through unified starter to honor intro and start loop when ready.
                            queueMainStart();
                        }
                    }
                }
                catch (err)
                {}
            }
            catch (err)
            {
                mainDesiredSrcRef.current = null;
            }
        };
        window.addEventListener('main-class-selected', onMainClassSelected);
        return () => window.removeEventListener('main-class-selected', onMainClassSelected);
    }, [queueMainStart, preloadMainClassAudio, stopMainLoop]);

    useEffect(() =>
    {
        const onMainThemeFadeOut = (e) =>
        {
            const requestedDuration = e && e.detail ? Number(e.detail.durationMs) : NaN;
            fadeOutAndStopMainLoop(Number.isFinite(requestedDuration) ? requestedDuration : MAIN_THEME_EXIT_FADE_MS);
        };

        window.addEventListener('main-theme-fade-out', onMainThemeFadeOut);
        return () => window.removeEventListener('main-theme-fade-out', onMainThemeFadeOut);
    }, [fadeOutAndStopMainLoop]);

    useEffect(() =>
    {
        if (isMainThemeRoute(currentPath))
        {
            const from = (window.history && window.history.state && window.history.state.from) || null;
            const routeFrom = previousPathRef.current;
            if (from === '/discord' || routeFrom === '/discord') return;

            if (mainSelectionRouteRef.current !== currentPath) return;
            if (mainQueuedSelectionVersionRef.current === mainSelectionVersionRef.current) return;
            mainQueuedSelectionVersionRef.current = mainSelectionVersionRef.current;
            queueMainStart();
        }
        else if (currentPath !== '/discord')
        {
            stopMainLoop();
            // clear selection so return to /main waits for fresh class dispatch
            mainDesiredSrcRef.current = null;
            mainIntroSrcRef.current = null;
            mainIntroPlayedRef.current = false;
            mainBaseVolumeRef.current = 0.5;
            mainStartDelayMsRef.current = 0;
            mainOnBeatEntryRef.current = null;
            mainRestartFromIntroRef.current = false;
            mainSelectedClassNameRef.current = 'Default';
            mainSelectionRouteRef.current = null;
            mainSelectionVersionRef.current = 0;
            mainQueuedSelectionVersionRef.current = 0;
        }
    }, [currentPath, queueMainStart, stopMainLoop]);

    useEffect(() => () =>
    {
        clearPendingMainStart();
    }, [clearPendingMainStart]);

    return ( <AchievementsProvider >
            <div className = "App" >
            {
                !isFRncRoute && !introDone && !shouldSkipRootIntroOnMobile && ( <IntroOverlay onComplete = {
                        handleIntroComplete
                    }
                    onFadeStart = {
                        handleOverlayFadeStart
                    }
                    playRequestedKey = {
                        playRequestedKey
                    }
                    onAutoplayBlocked = {
                        () =>
                        {
                            // video autoplay blocked prompt the fucker to enable media before proceeding
                            setVideoAutoplayBlocked(true);
                            setRequireEnable(true);
                        }
                    }
                    onAutoplayAllowed = {
                        () =>
                        {
                            setVideoAutoplayBlocked(false);
                            if (!autoplayBlocked) setRequireEnable(false);
                        }
                    }
                    />
                )
            }

            {
                /* render above everything (overlay has zIndex 20000 BC IT WASNT WORKING) */ }
            {
                !isFRncRoute && requireEnable && ( <div style = {
                        {
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.85)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 30001
                        }
                    } >
                    <div style = {
                        {
                            maxWidth: 480,
                            padding: 24,
                            background: '#111',
                            borderRadius: 8,
                            textAlign: 'center'
                        }
                    }
                    role = "dialog"
                    aria-modal = "true" >
                    <h2 style = {
                        {
                            marginTop: 0
                        }
                    } > Hold up! </h2> <p > Hey!It looks like your browser is blocking autoplay
                    for audio and video.To
                    let the site to work properly, allow audio and video to
                    function ! </p>
                    {
                        enableError && <div style = {
                                {
                                    color: '#f99',
                                    marginBottom: 8
                                }
                            } >
                            {
                                enableError
                            } </div>} <button onClick = {
                                handleEnableMedia
                            }
                        style = {
                                {
                                    marginTop: 12,
                                    padding: '8px 16px'
                                }
                            } > Enable audio & video </button> </div> </div>
                    )
                }

                {
                    /* This shit controls for the spaceDots shown in the background!!
                                SpaceDots should be visible on all pages, while the main
                                header and Ari only appear on root and /main. */
                }
                {
                    !isFRncRoute && !isSpotTheFloofRoute && (mountSpace || introDone) && ( <div style = {
                            {
                                opacity: fadeStarted ? 1 : 0,
                                transition: `opacity ${SPACE_FADE_MS}ms ease`,
                                pointerEvents: introDone ? 'auto' : 'none',
                            }
                        } >
                        <SpaceDots ref = {
                            spaceDotsRef
                        }
                        count = {
                            120
                        }
                        color = "#d4d4d4"
                        minSize = {
                            0.9
                        }
                        maxSize = {
                            3.0
                        }
                        speedFactor = {
                            originalSpaceSpeedRef.current
                        }
                        motion = {
                            spaceMotion
                        }
                        centerSpawnRadiusFactor = {
                            0.60
                        }
                        debugShowSpawnRadius = {
                            false
                        }
                        />
                        {
                            currentPath === '/' && ariMounted && ( <div style = {
                                    {
                                        opacity: ariVisible ? 1 : 0,
                                        transition: 'opacity 360ms ease'
                                    }
                                } >
                                <FloatingImages src = {
                                    '/AriFloats.png'
                                }
                                count = {
                                    1
                                }
                                speed = {
                                    0.08
                                }
                                scale = {
                                    rootFloatingAriScale
                                }
                                /> </div>
                            )
                        }
                        {
                            currentPath === '/' && headerMounted && ( <div style = {
                                    {
                                        opacity: headerVisible ? 1 : 0,
                                        transition: 'opacity 280ms ease',
                                        pointerEvents: headerVisible ? 'auto' : 'none'
                                    }
                                } >
                                <div style = {
                                    {
                                        position: 'fixed',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: 'none'
                                    }
                                } >
                                <div style = {
                                    rootHeaderStageStyle
                                } >
                                <header className = "App-header"
                                style = {
                                    rootHeaderStyle
                                } >
                                <img ref = {
                                    heartImgRef
                                }
                                src = "/Heart.png"
                                className = "Heart-image"
                                alt = "heart"
                                style = {
                                    rootHeartStyle
                                }
                                /> <p className = "typing" >
                                <span >
                                {
                                    typed
                                }</span><span className = "typing-cursor"
                                aria-hidden = "true" > | </span> </p> <a className = "App-link"
                                href = "https://arielwolf24.carrd.co/"
                                target = "_blank"
                                rel = "noopener noreferrer"
                                style = {
                                    {
                                        pointerEvents: 'auto'
                                    }
                                } >
                                My socials and other stuff!
                                </a> <div style = {
                                    {
                                        marginTop: 8
                                    }
                                } >
                                <div style = {
                                    {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                        alignItems: 'center'
                                    }
                                } >
                                {
                                    /* Start button - navigates to /main. (ONE WAY TRIP, or it should be if i forgor to remove the return button in /main)*/ } <button ref = {
                                    startButtonRef
                                }
                                className = "start-button"
                                onClick = {
                                    handleStartSequence
                                }
                                aria-label = "Start"
                                style = {
                                    {
                                        marginTop: 4,
                                        padding: '16px 42px',
                                        fontSize: '1.2rem',
                                        fontWeight: 600,
                                        borderRadius: 12,
                                        letterSpacing: '0.6px',
                                        cursor: 'pointer',
                                        background: '#ffffff10',
                                        color: '#fff',
                                        border: '1px solid #ffffff40',
                                        backdropFilter: 'blur(4px)',
                                        boxShadow: '0 4px 14px -4px rgba(0,0,0,0.6)',
                                        transition: 'transform 160ms ease, background 240ms ease, filter 200ms ease',
                                        pointerEvents: 'auto',
                                    }
                                }
                                onPointerDown = {
                                    (e) =>
                                    {
                                        e.currentTarget.style.transform = 'scale(0.96)';
                                    }
                                }
                                onPointerUp = {
                                    (e) =>
                                    {
                                        e.currentTarget.style.transform = 'scale(1)';
                                    }
                                }
                                onMouseEnter = {
                                    (e) =>
                                    {
                                        e.currentTarget.style.background = '#ffffff25';
                                        // hover2 stage: only when cursor is on the button
                                        try
                                        {
                                            setHeartBeatStage('hover2');
                                        }
                                        catch (err)
                                        {}
                                        try
                                        {
                                            setHoverTrackGain('h2', 1.0, 600);
                                        }
                                        catch (err)
                                        {}
                                    }
                                }
                                onMouseLeave = {
                                    (e) =>
                                    {
                                        e.currentTarget.style.background = '#ffffff10';
                                        e.currentTarget.style.transform = 'scale(1)';
                                        // revert to hover1 if still inside radius, else base
                                        try
                                        {
                                            setHeartBeatStage(ringHoverActiveRef.current ? 'hover1' : 'base');
                                        }
                                        catch (err)
                                        {}
                                        try
                                        {
                                            setHoverTrackGain('h2', 0.0, 600);
                                        }
                                        catch (err)
                                        {}
                                    }
                                } >
                                Begin </button>

                                </div> </div> </header> </div> </div> <div style = {
                                    rootCopyrightStyle
                                } > ©Arielwolf24 2018 - 2026. <span style = {
                                    {
                                        textDecoration: 'underline'
                                    }
                                } > All rights reserved. </span> </div> </div>
                            )
                        }

                        {
                            (autoplayBlocked || enableError) && ( <div style = {
                                    {
                                        position: 'fixed',
                                        bottom: 12,
                                        right: 12,
                                        background: 'rgba(255,255,255,0.95)',
                                        padding: 8,
                                        borderRadius: 8,
                                        zIndex: 9999,
                                        minWidth: 180
                                    }
                                } >
                                {
                                    autoplayBlocked && <div style = {
                                        {
                                            fontSize: 12,
                                            color: '#444',
                                            marginTop: 6
                                        }
                                    } > Autoplay blocked— sound will start when allowed </div>}
                                    {
                                        enableError && <div style = {
                                                {
                                                    fontSize: 13,
                                                    color: '#a00',
                                                    marginTop: 6
                                                }
                                            } >
                                            {
                                                enableError
                                            } </div>} </div>
                                    )
                                }

                                {
                                    /* requireEnable modal removed from here rendered at top-level so it overlays the intro video */ }
                                {
                                    /* keep background mounted above routes so SpaceDots stays persistent */ } <Router >
                                <Route path = "/" >
                                {
                                    /* Root content is already the header area above. Nothing extra needed. */ } </Route> <Route path = "/main" >
                                <div style = {
                                    {
                                        position: 'relative',
                                        zIndex: 2
                                    }
                                } >
                                <Main />
                                </div> </Route> <Route path = "/FullMoonClub" >
                                <SubpageFade >
                                <FullMoonClub />
                                </SubpageFade> </Route> <Route path = "/discord" >
                                <SubpageFade >
                                <Discord />
                                </SubpageFade> </Route> <Route path = "/AboutMe" >
                                <SubpageFade >
                                <AboutMe />
                                </SubpageFade> </Route> <Route path = "/Projects" >
                                <SubpageFade >
                                <Projects />
                                </SubpageFade> </Route> <Route path = "/ClickTheIdiot" >
                                <SubpageFade >
                                <ClickTheIdiot />
                                </SubpageFade> </Route> <Route path = "/settings" >
                                <SubpageFade >
                                <Settings />
                                </SubpageFade> </Route> <Route path = "/FurAffinityWarning" >
                                {
                                    /*hidden & scaled up for 1s, then snaps in and eases to normal scale*/ } <FurAffinityWarningSequence />
                                </Route> </Router>
                                {
                                    /* RGL route removed here so video remains mounted when background unmounts. */ } </div>
                            )
                        }
                        {
                            /* Render RGL route outside background block so video persists during fade */ } <Router >
                        <LeoLoadingShit
                        visible = { showLeoLoader }
                        mode = { leoLoaderMode }
                        position = { leoLoaderPosition }
                        showText = { false }
                        onFinished = { () => setShowLeoLoader(false) }
                        />
                        <Route path = "/FR*nc" >
                        <RedirectRoute to = "/PCUplaybook" />
                        </Route> <Route path = "/PCUplaybook" >
                        <FRncSuperSecretStrats />
                        </Route> <Route path = "/SpotTheFloof" >
                        <SpotTheFloof />
                        </Route> <Route path = "/RGL"
                        allowChildRoutes >
                        {
                            rglDirectEntryReady ? ( <div style = {
                                    {
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 10000,
                                        pointerEvents: 'auto'
                                    }
                                } >
                                <RGL />
                                </div>
                            ) : ( <div style = {
                                    {
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 10000,
                                        background: '#000',
                                        pointerEvents: 'none'
                                    }
                                }
                                />
                            )
                        } </Route> <Route path = "/Ragnarok" >
                        <RedirectRoute to = "/main" />
                        </Route> </Router>

                        {
                            /*fixed bottom-left across the site*/ }
                        {
                            showMainBpmDebug && isMainThemeRoute(currentPath) && ( <div style = {
                                    {
                                        position: 'fixed',
                                        top: 12,
                                        right: 12,
                                        zIndex: 45000,
                                        pointerEvents: 'none',
                                        padding: '10px 12px',
                                        border: '1px solid rgba(255,255,255,0.28)',
                                        background: 'rgba(0,0,0,0.62)',
                                        color: '#fff',
                                        fontSize: '0.92rem',
                                        lineHeight: 1.35,
                                        textAlign: 'right',
                                        borderRadius: 10,
                                        backdropFilter: 'blur(8px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
                                    }
                                } >
                                <div style = {
                                    {
                                        fontWeight: 700,
                                        letterSpacing: '0.04em'
                                    }
                                } > BPM DEBUG </div> <div >
                                {
                                    mainBpmDebugSnapshot.className
                                } </div>
                                {
                                    mainBpmDebugSnapshot.enabled ? ( <>
                                        <div style = {
                                            {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 10,
                                                whiteSpace: 'nowrap',
                                                marginTop: 2
                                            }
                                        } >
                                        <span style = {
                                            {
                                                opacity: 0.86
                                            }
                                        } > BPM: </span> <span style = {
                                            {
                                                marginLeft: 8,
                                                fontVariantNumeric: 'tabular-nums',
                                                textAlign: 'right'
                                            }
                                        } >
                                        {
                                            mainBpmDebugSnapshot.bpm
                                        } </span> </div> <div style = {
                                            {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 10,
                                                whiteSpace: 'nowrap',
                                                marginTop: 2
                                            }
                                        } >
                                        <span style = {
                                            {
                                                opacity: 0.86
                                            }
                                        } > Beat: </span> <span style = {
                                            {
                                                marginLeft: 8,
                                                fontVariantNumeric: 'tabular-nums',
                                                textAlign: 'right'
                                            }
                                        } >
                                        {
                                            mainBpmDebugSnapshot.currentBeat || '--'
                                        } </span> </div> <div style = {
                                            {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 10,
                                                whiteSpace: 'nowrap',
                                                marginTop: 2
                                            }
                                        } >
                                        <span style = {
                                            {
                                                opacity: 0.86
                                            }
                                        } > Loop Beat: </span> <span style = {
                                            {
                                                marginLeft: 8,
                                                fontVariantNumeric: 'tabular-nums',
                                                textAlign: 'right'
                                            }
                                        } >
                                        {
                                            mainBpmDebugSnapshot.targetBeat
                                        } </span> </div> <div style = {
                                            {
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 10,
                                                whiteSpace: 'nowrap',
                                                marginTop: 2
                                            }
                                        } >
                                        <span style = {
                                            {
                                                opacity: 0.86
                                            }
                                        } > Status: </span> <span style = {
                                            {
                                                marginLeft: 8,
                                                textAlign: 'right'
                                            }
                                        } >
                                        {
                                            mainBpmDebugSnapshot.loopStarted ? 'Looping' : 'Armed'
                                        } </span> </div> </>
                                    ) : ( <div > BPM Disabled </div>
                                    )
                                } <div style = {
                                    {
                                        opacity: 0.7
                                    }
                                } > Shift + B </div> </div>
                            )
                        } <div style = {
                            {
                                position: 'fixed',
                                left: 12,
                                bottom: 12,
                                zIndex: 40000,
                                pointerEvents: 'none'
                            }
                        } >
                        <div style = {
                            {
                                transform: 'scale(1)',
                                transformOrigin: 'bottom left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 6,
                                background: 'transparent',
                                pointerEvents: 'none'
                            }
                        } >
                        <VolumeControl size = {
                            220
                        }
                        /> </div> </div> </div> <Analytics />
                        </AchievementsProvider>
                    );
                }

                function FurAffinityWarningSequence()
                {
                    const [phase, setPhase] = React.useState('hidden'); // 'hidden' -> 'jump' -> 'settle'
                    const warningAudioRef = React.useRef(null);
                    const warningScareAudioRef = React.useRef(null);
                    const warningLoopStartTimerRef = React.useRef(null);
                    const [vignetteVisible, setVignetteVisible] = React.useState(false);
                    const [warningLoopPulseActive, setWarningLoopPulseActive] = React.useState(false);

                    React.useEffect(() =>
                    {
                        const t1 = setTimeout(() => setPhase('jump'), 1000); // wait 1s hidden
                        const t2 = setTimeout(() => setPhase('settle'), 1120); // quick snap towards normal
                        // preload scare and loop audio on mount so the jump can hit immediately.
                        const loopAudio = new Audio('/Audio/WarningLoop.ogg');
                        loopAudio.loop = true;
                        loopAudio.preload = 'auto';
                        loopAudio._baseVolume = 1.3;
                        try
                        {
                            loopAudio.volume = loopAudio._baseVolume * masterVolume.getMasterVolume();
                        }
                        catch (e)
                        {}
                        try
                        {
                            loopAudio.load();
                        }
                        catch (e)
                        {}
                        warningAudioRef.current = loopAudio;
                        try
                        {
                            masterVolume.applyToElement(loopAudio);
                        }
                        catch (e)
                        {}

                        const scareAudio = new Audio('/Audio/WarningScare.ogg');
                        scareAudio.loop = false;
                        scareAudio.preload = 'auto';
                        scareAudio._baseVolume = 1;
                        try
                        {
                            scareAudio.volume = scareAudio._baseVolume * masterVolume.getMasterVolume();
                        }
                        catch (e)
                        {}
                        try
                        {
                            scareAudio.load();
                        }
                        catch (e)
                        {}
                        warningScareAudioRef.current = scareAudio;
                        try
                        {
                            masterVolume.applyToElement(scareAudio);
                        }
                        catch (e)
                        {}

                        return () =>
                        {
                            clearTimeout(t1);
                            clearTimeout(t2);
                            setWarningLoopPulseActive(false);
                            if (warningLoopStartTimerRef.current)
                            {
                                clearTimeout(warningLoopStartTimerRef.current);
                                warningLoopStartTimerRef.current = null;
                            }
                            if (warningAudioRef.current)
                            {
                                try
                                {
                                    warningAudioRef.current.pause();
                                    warningAudioRef.current.currentTime = 0;
                                }
                                catch (e)
                                {}
                                warningAudioRef.current = null;
                            }
                            if (warningScareAudioRef.current)
                            {
                                try
                                {
                                    warningScareAudioRef.current.pause();
                                    warningScareAudioRef.current.currentTime = 0;
                                }
                                catch (e)
                                {}
                                warningScareAudioRef.current = null;
                            }
                        };
                    }, []);

                    // fire the scare when the jump happens, then bring in the loop shortly after.
                    React.useEffect(() =>
                    {
                        if (phase !== 'jump') return;

                        const scareAudio = warningScareAudioRef.current;
                        if (scareAudio)
                        {
                            try
                            {
                                scareAudio.currentTime = 0;
                            }
                            catch (e)
                            {}
                            scareAudio.play().catch(() =>
                            {});
                        }

                        if (warningLoopStartTimerRef.current)
                        {
                            clearTimeout(warningLoopStartTimerRef.current);
                            warningLoopStartTimerRef.current = null;
                        }

                        warningLoopStartTimerRef.current = setTimeout(() =>
                        {
                            warningLoopStartTimerRef.current = null;
                            const loopAudio = warningAudioRef.current;
                            if (!loopAudio) return;
                            try
                            {
                                loopAudio.currentTime = 0;
                            }
                            catch (e)
                            {}
                            loopAudio.play().catch(() =>
                            {});
                            setWarningLoopPulseActive(true);
                        }, 1000);
                    }, [phase]);

                    // control vignette: appear instantly with UI, then fade out after 0.3s
                    React.useEffect(() =>
                    {
                        if (phase === 'hidden') return;
                        setVignetteVisible(true);
                        const t = setTimeout(() => setVignetteVisible(false), 10);
                        return () => clearTimeout(t);
                    }, [phase]);

                    let scale = 1;
                    let opacity = 1;
                    let transition = 'transform 420ms cubic-bezier(0.21, 1.02, 0.35, 1.0)';

                    if (phase === 'hidden')
                    {
                        scale = 1.18; // slightly larger while hidden
                        opacity = 0;
                        transition = 'none';
                    }
                    else if (phase === 'jump')
                    {
                        scale = 1.02;
                        opacity = 1;
                        transition = 'transform 90ms cubic-bezier(0.05, 0.9, 0.3, 1.3), opacity 60ms linear';
                    }
                    else // settle
                    {
                        scale = 1;
                        opacity = 1;
                    }

                    return ( <div style = {
                            {
                                position: 'fixed',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'auto'
                            }
                        } >
                        <style >
                        {
                            `
        @keyframes aw24WarningLoopPulse {
          0% { opacity: 0.10; }
          50% { opacity: 0.50; }
          100% { opacity: 0.10; }
        }
      `
                        } </style>
                        {
                            /* Full-screen vignette background tinted dark red, scales with viewport */ } <div style = {
                            {
                                position: 'absolute',
                                inset: 0,
                                background: 'radial-gradient(circle at center, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.02) 35%, rgba(120,0,0,0.85) 100%)',
                                mixBlendMode: 'screen',
                                opacity: vignetteVisible ? 1 : (warningLoopPulseActive ? 0.10 : 0),
                                animation: warningLoopPulseActive ? 'aw24WarningLoopPulse 5.2s ease-in-out infinite' : 'none',
                                transition: vignetteVisible ? 'none' : 'opacity 900ms ease-out',
                                pointerEvents: 'none',
                            }
                        }
                        /> <div style = {
                            {
                                maxWidth: 640,
                                padding: 24,
                                background: 'rgba(0,0,0,0.7)',
                                borderRadius: 12,
                                color: '#fff',
                                textAlign: 'center',
                                fontSize: '1.05rem',
                                transform: `scale(${scale})`,
                                opacity,
                                transformOrigin: 'center',
                                transition,
                                zIndex: 1,
                            }
                        } >
                        <h1 style = {
                            {
                                marginTop: 0,
                                marginBottom: 16
                            }
                        } > WOAH HOLD ON THERE! </h1> <p >
                        Fur Affinity is a website that contains Adult Content otherwise Sensitive Content. <br />
                        This site is not suitable
                        for underage users! <br />
                        <br />
                        I am not responsible
                        for any awakenings you may have on that site. <br />
                        <br />
                        <span style = {
                            {
                                display: 'inline-block',
                                marginTop: 10,
                                fontSize: '1.2rem',
                                fontWeight: 800
                            }
                        } >
                        YOU HAVE BEEN WARNED </span> </p> <p style = {
                            {
                                marginTop: 20
                            }
                        } > Do you want to
                        continue ? </p> <div style = {
                                {
                                    marginTop: 20,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }
                            } >
                            <a
                        href = "https://www.furaffinity.net/user/arielwolf24"
                        target = "_blank"
                        rel = "noopener noreferrer"
                        style = {
                            {
                                padding: '10px 18px',
                                borderRadius: 8,
                                background: '#c62828',
                                color: '#fff',
                                fontWeight: 600,
                                textDecoration: 'none',
                            }
                        } >
                        I know what I 'm doing, take me there! </a> <button type = "button"
                        onClick = {
                            () => navigate('/main')
                        }
                        style = {
                            {
                                padding: '10px 18px',
                                borderRadius: 8,
                                background: 'transparent',
                                border: '1px solid #fff',
                                color: '#fff',
                                cursor: 'pointer',
                            }
                        } >
                        FUCK GO BACK </button> </div> </div> </div>
                    );
                }

                function RedirectRoute(
                {
                    to
                })
                {
                    useLayoutEffect(() =>
                    {
                        replaceNavigate(to);
                    }, [to]);

                    return null;
                }

                function App()
                {
                    const [mediaGateState, setMediaGateState] = useState('checking');
                    const [mediaGateError, setMediaGateError] = useState('');

                    const probeMediaPlaybackAccess = useCallback(async () =>
                    {
                        const audio = new Audio(MEDIA_GATE_AUDIO_PROBE_SRC);
                        const video = document.createElement('video');

                        try
                        {
                            audio.preload = 'auto';
                            audio.loop = false;
                            audio.volume = 0.001;
                            audio.muted = false;
                            try
                            {
                                audio.currentTime = 0;
                            }
                            catch (e)
                            {}
                            await audio.play();
                            try
                            {
                                audio.pause();
                            }
                            catch (e)
                            {}
                            try
                            {
                                audio.currentTime = 0;
                            }
                            catch (e)
                            {}

                            video.src = MEDIA_GATE_VIDEO_PROBE_SRC;
                            video.preload = 'metadata';
                            video.playsInline = true;
                            video.volume = 0.001;
                            video.muted = false;
                            try
                            {
                                video.currentTime = 0;
                            }
                            catch (e)
                            {}
                            await video.play();
                            try
                            {
                                video.pause();
                            }
                            catch (e)
                            {}
                            try
                            {
                                video.currentTime = 0;
                            }
                            catch (e)
                            {}

                            return true;
                        }
                        finally
                        {
                            try
                            {
                                audio.pause();
                            }
                            catch (e)
                            {}
                            try
                            {
                                audio.src = '';
                            }
                            catch (e)
                            {}
                            try
                            {
                                audio.load();
                            }
                            catch (e)
                            {}
                            try
                            {
                                video.pause();
                            }
                            catch (e)
                            {}
                            try
                            {
                                video.removeAttribute('src');
                            }
                            catch (e)
                            {}
                            try
                            {
                                video.load();
                            }
                            catch (e)
                            {}
                        }
                    }, []);

                    const checkMediaAccess = useCallback(async (
                    {
                        userInitiated = false
                    } = {}) =>
                    {
                        setMediaGateError('');

                        const autoplayPolicy = getMediaAutoplayPolicy();
                        if (!userInitiated && autoplayPolicy === 'allowed')
                        {
                            setMediaGateState('ready');
                            return true;
                        }

                        setMediaGateState('checking');

                        try
                        {
                            await probeMediaPlaybackAccess();
                            setMediaGateState('ready');
                            return true;
                        }
                        catch (err)
                        {
                            setMediaGateState('blocked');
                            setMediaGateError('We currently dont have natural access to play custom audio or video :(');
                            return false;
                        }
                    }, [probeMediaPlaybackAccess]);

                    useEffect(() =>
                    {
                        let cancelled = false;

                        const runInitialCheck = async () =>
                        {
                            try
                            {
                                const autoplayPolicy = getMediaAutoplayPolicy();
                                if (autoplayPolicy === 'allowed')
                                {
                                    if (!cancelled) setMediaGateState('ready');
                                    return;
                                }

                                await checkMediaAccess();
                            }
                            catch (e)
                            {
                                if (!cancelled)
                                {
                                    setMediaGateState('blocked');
                                    setMediaGateError('We currently dont have natural access to play custom audio or video :(');
                                }
                            }
                        };

                        runInitialCheck();
                        return () =>
                        {
                            cancelled = true;
                        };
                    }, [checkMediaAccess]);

                    const handleEnableSiteMedia = useCallback(async () =>
                    {
                        await checkMediaAccess(
                        {
                            userInitiated: true
                        });
                    }, [checkMediaAccess]);

                    if (mediaGateState !== 'ready')
                    {
                        const isChecking = mediaGateState === 'checking';

                        return ( <div className = "App"
                            style = {
                                {
                                    position: 'fixed',
                                    inset: 0,
                                    background: '#000',
                                    color: '#fff'
                                }
                            } >
                            <div style = {
                                {
                                    position: 'fixed',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 24,
                                    background: 'rgba(0,0,0,0.92)',
                                    zIndex: 50000,
                                }
                            } >
                            <div style = {
                                {
                                    maxWidth: 560,
                                    padding: 28,
                                    background: '#111',
                                    borderRadius: 12,
                                    textAlign: 'center',
                                    border: '1px solid rgba(255,255,255,0.12)'
                                }
                            }
                            role = "dialog"
                            aria-modal = "true" >
                            <h2 style = {
                                {
                                    marginTop: 0,
                                    marginBottom: 12
                                }
                            } > Site needs permissions! </h2> <p style = {
                                {
                                    marginTop: 0,
                                    lineHeight: 1.5
                                }
                            } >
                            Heya!Your browser is currently blocking audio and video playback, which is required
                            for most pages to properly work.If you would be great to set permissions
                            for audio and video to always allow, it would do wonders
                            for
                            you as a visitor!Otherwise, you can
                            continue with temporary access. </p>
                                {
                                    mediaGateError && <div style = {
                                            {
                                                color: '#ff0000',
                                                marginTop: 12,
                                                lineHeight: 1.45
                                            }
                                        } >
                                        {
                                            mediaGateError
                                        } </div>}
                                        {
                                            isChecking ? ( <div style = {
                                                    {
                                                        marginTop: 16,
                                                        color: '#ffee00'
                                                    }
                                                } > Checking to make sure... </div>
                                            ) : ( <button onClick = {
                                                    handleEnableSiteMedia
                                                }
                                                style = {
                                                    {
                                                        marginTop: 16,
                                                        padding: '10px 18px'
                                                    }
                                                } >
                                                Allow audio & video </button>
                                            )
                                        } </div> </div> </div>
                                );
                        }

                        return <AppContent /> ;
                    }

                    export default App;
